import {
  Fragment,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  splitNeoChartContent,
  type NeoAnswerChart,
  type NeoUndoChatNote,
  type NeoVisualGroup,
} from '@neomaris/protocol';

import { type NeoLabelChange } from '../lib/neoNames';
import { NeoLabelChangeCard } from './NeoLabelChangeCard';

import { api } from '../api';
import { useAuth, useDemoGuest } from '../auth';
import {
  NEO_AGENTS_CAPABILITY,
  neoAgentBusyLine,
  type NeoAgentAtWork,
} from '../lib/neoAgentProgress';
import { neoChartCards } from '../lib/neoAnswerCharts';
import { selectNeoChartView } from '../lib/neoChartViews';
import {
  buildLooksReceipt,
  looksTurnRef,
  supersededLooksParts,
  wholeTurnUndoRefs,
  type LooksReceiptPart,
} from '../lib/neoLooksReceipt';
import {
  answerShowsCard,
  neoAnswerCardCapabilities,
  neoAnswerCardPinnable,
  type NeoAnswerCard,
  type NeoCameraSnapshotCard,
  type NeoChatPageCard,
  type NeoDiagramCard,
  type NeoMapCard,
  type NeoPictureCard,
} from '../lib/neoAnswerCards';
import {
  NEO_ANSWERS_CAPABILITY,
  groupNeoAnswers,
  neoAnswerChips,
  neoAnswerCopyText,
  neoChatTitle,
  neoConclusion,
  neoSourceLine,
} from '../lib/neoAnswers';
import {
  neoAnswerHasDetails,
  neoBehindDefaultTarget,
  neoBehindSlideInTarget,
  neoBehindFollowTarget,
  neoNewestAnswerWithDetails,
} from '../lib/neoBehindAnswer';
import { neoFeedbackAskAfterRating, type NeoFeedbackDetail } from '../lib/neoFeedback';
import {
  neoConversationMessagesUrl,
  neoHistoryOutcome,
  withLaterNeoHistory,
  withNeoHistory,
  type NeoHistoryRead,
} from '../lib/neoChatHistory';
import {
  NEO_GREETING_PENDING_EVENT,
  NEO_GREETING_WAIT_MS,
  NEO_MAIN_MESSAGE_EVENT,
  fetchMainNeoConversation,
  forgetMainNeoConversation,
  newNeoChatPath,
  type NeoGreetingPendingDetail,
  type NeoMainMessageDetail,
} from '../lib/neoMainChat';
import {
  clearNeoConversationId,
  isNeoConversationGone,
  loadNeoConversationId,
  saveNeoConversationId,
} from '../lib/neoChatResume';
import { NEO_UNDO_NOTE_EVENT, undoChatNoteLine } from '../lib/neoUndoNote';
import { getBoatPresence } from '../lib/boatPresence';
import {
  askNeoPanelQuestion,
  boardBesideChatContext,
  isBoardEditContext,
  NEO_EDIT_CHAT_CLOSED_EVENT,
  NEO_EDIT_SCOPE_EVENT,
  NEO_EDIT_UNSELECT_EVENT,
  neoPanelCopy,
  type NeoEditScopeDetail,
  type NeoEditTarget,
  type NeoRequestContext,
} from '../lib/neoPanel';
import {
  keepNeoPanelQuestionLast,
  neoPanelQuestionContext,
  withNeoPanelQuestion,
} from '../lib/neoPanelQuestion';
import {
  buildReceipt,
  notebookTurnRefs,
  supersededParts,
  receiptTurnRef,
  type ReceiptPart,
} from '../lib/neoReceipt';
import {
  NEO_RECALL_NOTICE,
  NEO_RECALL_NOTICE_LINK,
  dismissNeoRecallNotice,
  readNeoRecallNoticeDismissed,
  showNeoRecallNotice,
} from '../lib/neoRecall';
import { readNeoStream, type NeoStreamEvent } from '../lib/neoStream';
import {
  NEO_TURN_POLL_MS,
  NEO_TURN_RECOVERY_MS,
  neoTurnRecovery,
  neoTurnUrl,
  type NeoTurnRead,
  type NeoTurnRecovery,
} from '../lib/neoTurnRecovery';
import { useChatAttachments } from '../lib/useChatAttachments';
import { ChatComposer, type ChatComposerMenuItem } from './ChatComposer';
import { MessageAttachments } from './MessageAttachments';
import { useMediaQuery } from '../lib/useMediaQuery';
import { BoatChromeContext } from './app/boatChrome';
import { boatPath } from './app/shellNav';
import { NeoAlarmChangeCard } from './NeoAlarmChangeCard';
import { NeoAlarmProposalCard } from './NeoAlarmProposalCard';
import { useEditChatFrame } from './neo/editChatFrame';
import { NeoAnswerActions, NeoAnswerByline, NeoAnswerSourceLine } from './neo/NeoAnswerFrame';
import { NeoBehindAnswer } from './neo/NeoBehindAnswer';
import { NeoChatHeader } from './neo/NeoChatHeader';
import { NeoFollowups, type NeoAnswerViewHooks } from './neo/NeoFollowups';
import { NeoSuggestions } from './neo/NeoSuggestions';
import { NeoFeedback } from './NeoFeedback';
import { NeoLooksReceipt } from './NeoLooksReceipt';
import { NeoReceipt, useWholeTurnUndos } from './NeoReceipt';
import {
  DASHBOARD_UPDATED_EVENT,
  applyNeoStreamEvent,
  newNeoStreamTurn,
  showNeoTurnBubble,
  type ChatMessage,
  type FeedbackRating,
  type NeoStreamTurn,
  type NeoStreamTurnHost,
} from './neo/neoStreamTurn';
import { useNeoVoice, type NeoVoice, type NeoVoiceTurns } from '../lib/useNeoVoice';
import { NeoTalkButton, NeoVoiceStrip } from './neo/NeoVoiceStrip';
import { PinChartButton } from './PinChartButton';
import { Icon } from './ui/Icon';

interface NeoDeviceContext {
  displayName: string;
  protocol: string;
  manufacturerName: string | null;
  modelId: string | null;
  deviceFunctionName: string | null;
  lastSeenAt: string;
}

interface NeoBoatContext {
  displayName: string;
  deviceCount: number;
  devices: NeoDeviceContext[];
  truncatedDevices: boolean;
  telemetryCount: number;
  telemetry: NeoTelemetryContext[];
  truncatedTelemetry: boolean;
}

interface NeoTelemetryContext {
  path: string;
  value: string;
  unit: string | null;
  source: string | null;
  deviceId: string | null;
  sampledAt: string;
  ageMinutes: number;
  freshness: 'fresh' | 'stale' | 'old' | 'future';
}

interface NeoContext {
  contextCollectedAt: string;
  user: {
    displayName: string | null;
  };
  boats: NeoBoatContext[];
  // The owner's switch for summaries of past conversations (TOM-252), which
  // Neo now reads into its answers. Gates the one-time notice only.
  recallEnabled: boolean;
}

/** A follow-up chip's reference to the answer it came from (contract §2). */
interface NeoFollowupRef {
  messageId: string;
  index: number;
}

export { DASHBOARD_UPDATED_EVENT };

interface NeoChatProps {
  inlineLauncher?: boolean;
  /**
   * False renders no launcher button. The panel still opens on the
   * `nm:open-neo` event, so a page's own "Ask Neo" buttons open it (TOM-195).
   */
  launcher?: boolean;
  /**
   * Answers only a request that names a board: the board page's Edit chat,
   * beside the sparkle's main chat, which answers the rest (TOM-547).
   */
  editOnly?: boolean;
}

type ContextState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; context: NeoContext }
  | { kind: 'error'; message: string };

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Within this many px of its bottom, the log follows new lines. */
const STICK_DISTANCE = 80;

/**
 * The log's scroll position that puts an owner's message at its top, under
 * the log's padding and any notices held there (TOM-709); null when the
 * message is not in the log.
 */
function questionTop(log: HTMLElement, messageId: string | null): number | null {
  if (messageId === null) return null;
  const question = log.querySelector(`[data-neo-message="${CSS.escape(messageId)}"]`);
  if (question === null) return null;
  const notices = log.querySelector<HTMLElement>('.neo-page-notices');
  const inset =
    (Number.parseFloat(getComputedStyle(log).paddingTop) || 0) + (notices?.offsetHeight ?? 0);
  const top = question.getBoundingClientRect().top - log.getBoundingClientRect().top;
  return Math.max(0, Math.round(log.scrollTop + top - inset));
}

function NeoMarkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 3.2 20.8 20 12 16.5 3.2 20 12 3.2Z" />
      <path d="m12 7.7 3.7 7.1-3.7-1.5-3.7 1.5L12 7.7Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// The chips that record what Neo did (a note kept, a chart pinned, an alarm
// changed) all start "✓ ". They show as a quiet line with a check icon, not as
// an answer. The text is printed verbatim.
const NOTE_PREFIX = /^✓\s*/;

function isNoteMessage(message: ChatMessage): boolean {
  return (
    message.role === 'assistant' &&
    message.local === true &&
    (message.undoNote === true || NOTE_PREFIX.test(message.content))
  );
}

function newId(): string {
  return crypto.randomUUID();
}

function displayName(authName: string | null | undefined, contextName: string | null): string {
  return contextName ?? authName ?? 'there';
}

function greetingFor(context: NeoContext, authName: string | null | undefined): string {
  const name = displayName(authName, context.user.displayName);
  if (context.boats.length === 0) {
    return `Hi ${name}. I'm Neo, your boat assistant. Link a boat and I can help with maritime questions.`;
  }

  const boats = context.boats.map((boat) => boat.displayName).join(', ');
  return `Hi ${name}. I'm Neo, your boat assistant. I can help with ${boats}, connected devices, and latest known telemetry.`;
}

function contextUrl(boatId: string | undefined): string {
  return boatId === undefined ? '/v1/neo/context' : `/v1/neo/context?boatId=${boatId}`;
}

function safeBodyMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('message' in body)) return null;
  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' && message.trim().length > 0 ? message : null;
}

/** What a failed Neo turn or context read says in the chat. Exported for its test (TOM-868). */
export function friendlyNeoError(err: unknown, phase: 'chat' | 'context'): string {
  const e = err as { status?: number; body?: unknown; message?: string };
  if (e.status === 401) return 'Your session expired. Sign in again to keep using Neo.';
  if (e.status === 429) {
    return safeBodyMessage(e.body) ?? 'Neo is receiving too many requests. Try again shortly.';
  }
  if (e.status === 502 || e.status === 503 || e.status === 504) {
    return phase === 'chat'
      ? 'Neo could not reach the model provider. Your message was not sent; try again in a moment.'
      : 'Neo could not load boat context right now. Try again in a moment.';
  }
  const bodyMessage = safeBodyMessage(e.body);
  if (bodyMessage !== null) return bodyMessage;
  return phase === 'chat'
    ? 'Neo could not respond right now. Try again in a moment.'
    : 'Neo could not load its context. Try again in a moment.';
}

// Assistant turns arrive as Markdown — headings, GFM tables, bullet lists, bold
// (see the Neo system prompt) — so they must be rendered, not printed as raw
// `###` / `|---|` syntax. react-markdown is XSS-safe by default: it emits no raw
// HTML (no rehype-raw) and sanitizes link protocols, so untrusted model output
// cannot inject markup. It does draw `![alt](url)` as an <img>, though, which
// the `img` override below closes. remark-gfm adds tables + autolinks.
const NEO_MARKDOWN_PLUGINS = [remarkGfm];

const NEO_MARKDOWN_COMPONENTS: Components = {
  // Cited links open in a new tab and never leak the opener.
  a({ node: _node, ...props }) {
    return <a {...props} target="_blank" rel="noreferrer noopener" />;
  },
  // A Markdown image shows its alt text only, never an <img> (TOM-639, threat
  // model K17). An <img> fetches its model-written URL with no click, telling a
  // third party the owner's address and whatever the URL carries, and the SPA
  // sets no CSP to stop it. An image with no alt text shows nothing.
  img({ alt }) {
    const text = alt?.trim();
    return text ? <span className="neo-markdown-image-alt">{text}</span> : null;
  },
  // Wrap wide tables (e.g. the device list) so they scroll within the panel
  // instead of overflowing it.
  table({ node: _node, ...props }) {
    return (
      <div className="neo-markdown-table">
        <table {...props} />
      </div>
    );
  },
};

// The chart library loads with the first chart (ADR 0230), not with the chat:
// NeoChat is in the eager shell bundle (ADR 0109's lazy-load trigger).
// With the answer charts and views (TOM-711), which hand a base chart to NeoChart.
const NeoChartView = lazy(() => import('./neo/charts/NeoAnswerChartBlock'));
// The map card, and the anchor map's module it reaches, load with the first
// map (TOM-468); the GL bundle itself loads only when a map is drawn.
const NeoMapCardView = lazy(() => import('./map/NeoMapCard'));
// The diagram card loads with the first diagram (TOM-643).
const NeoDiagramCardView = lazy(() => import('./diagram/NeoDiagramCard'));
// The picture card loads with the first picture (TOM-646).
const NeoPictureCardView = lazy(() => import('./picture/NeoPictureCard'));
// The camera snapshot card loads with the first snapshot (TOM-830).
const NeoCameraSnapshotCardView = lazy(() => import('./picture/NeoCameraSnapshotCard'));
// The page card, and the page library's drawer, load with the first page (TOM-871).
const NeoChatPageCardView = lazy(() => import('./chatPage/NeoChatPageCard'));

const HAS_CHART_MARKER = /\{\{\s*chart\s*:/i;
// A chart or a card marker (ADR 0266). Of the cards the web draws maps
// (TOM-468), diagrams (TOM-643) and pictures (TOM-646); an answer the app's
// chat made can still reach a web thread, so its other card markers are split
// out and dropped rather than shown as text.
const HAS_REPLY_MARKER = /\{\{\s*(?:chart|card)\s*:/i;

/** Holds a chart's place while its record is on the way, at a chart's height. */
function NeoChartPlaceholder() {
  return <div className="neo-chart neo-chart--placeholder" aria-hidden="true" />;
}

/**
 * Holds a card's place while its record is on the way, at a map card's height:
 * the marker arrives before the record says what kind it is.
 */
function NeoCardPlaceholder() {
  return (
    <div className="neo-chart neo-chart--placeholder neo-map--placeholder" aria-hidden="true" />
  );
}

/**
 * Assistant markdown, scoped under `.neo-markdown` so model output renders at
 * chat scale (marketing.css owns the styles). Shared by every Neo surface —
 * a bare <ReactMarkdown> outside this wrapper falls through to the global
 * document typography (64px h1 inside a bubble), which is exactly the watch
 * page bug this export exists to prevent recurring.
 *
 * An answer may place charts with `{{chart:cN}}` markers (ADR 0230): the text
 * around them renders as Markdown and each marker becomes the chart it names.
 * While the answer is still streaming, a marker whose chart has not arrived
 * holds its place; once it is done, a marker naming no chart is dropped.
 *
 * With `cards`, a `{{card:kN}}` marker draws the map (TOM-468), the diagram
 * (TOM-643) or the picture (TOM-646) it names, held by a placeholder while the
 * answer streams. Without them (the Watch page, the admin chat) a card marker
 * is dropped, as before. Charts and cards sit in the answer at their markers,
 * under the words before them: the page's charts-left, words-right split is
 * gone (TOM-709).
 */
export function NeoMarkdown({
  content,
  charts,
  cards: answerCards,
  streaming = false,
  chartAction,
  cardAction,
  views,
  answerId,
  storedId,
  zone,
}: {
  content: string;
  charts?: readonly NeoAnswerChart[];
  /** The answer's cards of chart views (TOM-711): its record's `visuals`. */
  views?: readonly NeoVisualGroup[];
  /** The answer message's id, which keeps each card's chosen view (as its chips name it). */
  answerId?: string;
  /** The stored answer's id and its zone: a card's view survives a reload; a comparison's times are the boat's. */
  storedId?: string;
  zone?: string;
  /** The maps, diagrams, pictures, snapshots and pages this surface draws; undefined drops every card marker. */
  cards?: readonly NeoAnswerCard[];
  streaming?: boolean;
  /** A control in each chart's head — the Pin button, once the answer is stored. */
  chartAction?: (chart: NeoAnswerChart) => ReactNode;
  /** The same for a map, a diagram or a page of readings; a picture has none. */
  cardAction?: (card: NeoAnswerCard) => ReactNode;
}) {
  if (charts === undefined && !HAS_REPLY_MARKER.test(content)) {
    return (
      <div className="neo-markdown">
        <ReactMarkdown remarkPlugins={NEO_MARKDOWN_PLUGINS} components={NEO_MARKDOWN_COMPONENTS}>
          {content}
        </ReactMarkdown>
      </div>
    );
  }
  const all: ReactNode[] = [];
  const chartCards = neoChartCards(views, charts ?? []);
  splitNeoChartContent(content, { streaming }).forEach((segment, i) => {
    if (segment.kind === 'text') {
      all.push(
        <ReactMarkdown
          key={`t${i}`}
          remarkPlugins={NEO_MARKDOWN_PLUGINS}
          components={NEO_MARKDOWN_COMPONENTS}
        >
          {segment.text}
        </ReactMarkdown>
      );
      return;
    }
    // Of the cards (ADR 0266) the web draws maps (TOM-468), diagrams (TOM-643),
    // pictures (TOM-646), camera snapshots (TOM-830) and pages (TOM-871); a
    // surface that draws none drops every card marker.
    if (segment.kind === 'card') {
      if (answerCards === undefined) return;
      const card = answerCards.find((c) => c.id === segment.id);
      if (card === undefined) {
        if (streaming) all.push(<NeoCardPlaceholder key={segment.id} />);
        return;
      }
      all.push(
        <Suspense key={segment.id} fallback={<NeoCardPlaceholder />}>
          {card.spec.kind === 'picture' ? (
            // Never pinned, so it takes no action (TOM-646).
            <NeoPictureCardView card={card as NeoPictureCard} />
          ) : card.spec.kind === 'camera_snapshot' ? (
            // Never pinned either (TOM-830).
            <NeoCameraSnapshotCardView card={card as NeoCameraSnapshotCard} zone={zone} />
          ) : card.spec.kind === 'diagram' ? (
            <NeoDiagramCardView card={card as NeoDiagramCard} action={cardAction?.(card)} />
          ) : card.spec.kind === 'chat_page' ? (
            // Pinned only when it binds no worked-out figure (TOM-871).
            <NeoChatPageCardView card={card as NeoChatPageCard} action={cardAction?.(card)} />
          ) : (
            <NeoMapCardView card={card as NeoMapCard} action={cardAction?.(card)} />
          )}
        </Suspense>
      );
      return;
    }
    const chart = charts?.find((c) => c.id === segment.id);
    if (chart === undefined) {
      if (streaming) all.push(<NeoChartPlaceholder key={segment.id} />);
      return;
    }
    all.push(
      <Suspense key={segment.id} fallback={<NeoChartPlaceholder />}>
        <NeoChartView
          chart={chart}
          card={chartCards.get(chart.id)}
          answerId={answerId}
          storedId={storedId}
          zone={zone}
          action={chartAction?.(chart)}
        />
      </Suspense>
    );
  });
  return <div className="neo-markdown">{all}</div>;
}

function NeoMessageBody({
  message,
  streaming,
  content = message.content,
}: {
  message: ChatMessage;
  streaming: boolean;
  /** What to draw of an answer: its body, once the conclusion is drawn above it (TOM-709). */
  content?: string;
}) {
  // User text is shown verbatim (CSS `white-space: pre-wrap` keeps their line
  // breaks); only Neo's replies are treated as Markdown. A plain chip is
  // verbatim too, so stored text in it can never become an image the browser
  // fetches, or a link.
  if (message.role === 'user' || message.plain === true) return <>{message.content}</>;
  // A chart can be pinned once its answer is stored: the pin names that answer.
  const messageId = streaming ? undefined : message.messageId;
  return (
    <NeoMarkdown
      content={content}
      charts={message.charts}
      views={message.answer?.visuals}
      answerId={message.id}
      storedId={message.messageId}
      zone={message.answer?.source?.timeZone}
      cards={message.cards ?? []}
      streaming={streaming}
      chartAction={
        messageId === undefined
          ? undefined
          : (chart) => (
              <PinChartButton boatId={chart.spec.boatId} messageId={messageId} chartId={chart.id} />
            )
      }
      cardAction={
        messageId === undefined
          ? undefined
          : (card) =>
              // A picture is never pinned to a board (TOM-646), nor a page that
              // shows a worked-out figure (TOM-871): no Pin button.
              neoAnswerCardPinnable(card) ? (
                <PinChartButton boatId={card.spec.boatId} messageId={messageId} cardId={card.id} />
              ) : undefined
      }
    />
  );
}

/** A done turn's stored answer, read back by its ref (TOM-712). */
type NeoStoredAnswer = Extract<NeoTurnRecovery, { kind: 'answer' }>;

/** How long one read of a dropped turn may take before it counts as no response. */
const NEO_TURN_READ_TIMEOUT_MS = 10_000;

/**
 * One read of a streamed turn by its ref (contract §12.3.1, TOM-712). It gives
 * up at `signal` (Stop, leaving the chat) or after ten seconds, whichever is
 * first: a read with no response waits like a running turn.
 */
async function readNeoTurn(turnRef: string, signal: AbortSignal): Promise<NeoTurnRead> {
  try {
    return {
      ok: true,
      body: await api<unknown>(neoTurnUrl(turnRef), {
        signal: AbortSignal.any([signal, AbortSignal.timeout(NEO_TURN_READ_TIMEOUT_MS)]),
      }),
    };
  } catch (err) {
    return { ok: false, status: (err as { status?: number }).status };
  }
}

/** Waits `ms`, or less when `signal` aborts. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
  });
}

/**
 * What a chat's turns offer (§12.3): Neo's charts (ADR 0230), maps (TOM-468),
 * diagrams (TOM-643), pictures (TOM-646) and camera snapshots (TOM-830); the
 * other card kinds wait for TOM-195. Its busy row names a specialist at work,
 * or a picture or a page being drawn (`agent` frames, TOM-543). The chat page,
 * home's and the sparkle's also take each answer's record (TOM-709), and with
 * it Neo's pages (TOM-871, cloud ADR 0399 §8), which a chat claims only inside
 * `BoatShell`: never the one `AppShell` mounts on routes that are not a boat's.
 */
function neoChatCapabilities(answers: boolean, boatShell: boolean): string[] {
  return [
    'charts',
    'label_changes',
    ...neoAnswerCardCapabilities({ boatShell, answers }),
    NEO_AGENTS_CAPABILITY,
    ...(answers ? [NEO_ANSWERS_CAPABILITY] : []),
  ];
}

/** One read of a conversation's stored messages (contract §12.11). */
async function readNeoHistory(conversationId: string, boatId?: string): Promise<NeoHistoryRead> {
  try {
    return {
      ok: true,
      body: await api<unknown>(neoConversationMessagesUrl(conversationId, boatId)),
    };
  } catch (err) {
    const e = err as { status?: number; body?: unknown };
    return { ok: false, status: e.status, body: e.body };
  }
}

interface NeoConversationOptions {
  /** The boat the chat is scoped to; undefined for the chat with no boat. */
  boatId: string | undefined;
  /** True while the chat is on screen: the panel open, or the page mounted. */
  active: boolean;
  /**
   * A chat opened by its address (`/neo/:conversationId`, TOM-195). It is read
   * and continued by that id, and never read from or written to the main
   * chat's session record (neoChatResume.ts).
   */
  routeConversationId?: string;
  /**
   * A separate new chat (`neo/new`, TOM-517): it starts with no conversation,
   * and Neo's first answer gives it one, told to `onStarted`.
   */
  fresh?: boolean;
  onStarted?: (conversationId: string) => void;
  /**
   * Ask for each answer's record (the `answers` capability, TOM-709): its
   * source line and follow-ups, and the answer layout that shows them. The
   * full chat page, home's chat and the sparkle's ask; the board's Edit chat
   * does not.
   */
  answers?: boolean;
}

/**
 * One Neo conversation: its context, transcript, streaming turns and
 * composer state. The panel and the full page both run on this; each adds
 * only its own chrome.
 *
 * On a boat, the chat opened without an address is the boat's main chat,
 * which the server knows (TOM-517, ADR 0337): the same in every tab and on
 * every device, and where Neomaris posts an alarm's message. With no boat, the
 * chat keeps its conversation in this tab's session record, as before.
 */
function useNeoConversation({
  boatId,
  active,
  routeConversationId,
  fresh = false,
  onStarted,
  answers = false,
}: NeoConversationOptions) {
  const auth = useAuth();
  const navigate = useNavigate();
  // Inside BoatShell, where Neo's pages are claimed (TOM-871).
  const boatShell = useContext(BoatChromeContext) !== null;
  const userId = auth.user?.id ?? null;
  const routeChat = routeConversationId !== undefined;
  const main = !routeChat && !fresh && boatId !== undefined;
  // Only the chat with no boat keeps its conversation in the session record.
  const sessionKept = !routeChat && !fresh && boatId === undefined;
  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;
  // The conversation a new chat started here, which the page then opens by
  // its address: the chat goes on as it is, not read again from the server.
  const startedHereRef = useRef<string | null>(null);
  // Whose and which boat's conversation this chat continues after a reload
  // (TOM-252). Null until the account is known: nothing is kept then.
  const resumeScope = userId === null ? null : { userId, boatId: boatId ?? null };
  const [contextState, setContextState] = useState<ContextState>({
    kind: 'idle',
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [draft, setDraft] = useState('');
  // A seeded prompt to auto-send once ready (empty-until-Neo auto-compose,
  // ADR 0146). Held in state, not sent inline, so the send uses the current
  // `sendMessage` and respects the in-flight guard.
  const [pendingAutoSend, setPendingAutoSend] = useState<string | null>(null);
  // Neo's question after Ask Neo on a panel (TOM-958), held while an answer
  // is arriving or the chat is being read: added once the chat is free.
  const [pendingPanelQuestion, setPendingPanelQuestion] = useState<{
    title: string;
    context: NeoRequestContext;
  } | null>(null);
  // What is on screen, named with each request while the panel was opened for
  // it (cloud ADR 0302 §1): "Edit with Neo" on a board or on one of its items.
  // Never stored; the full chat page names nothing.
  const [requestContext, setRequestContext] = useState<NeoRequestContext | null>(null);
  const [sending, setSending] = useState(false);
  // How many of a voice call's questions are being answered (TOM-731). The
  // chat is busy while one is, as it is while a typed turn runs.
  const [voiceAnswering, setVoiceAnswering] = useState(0);
  const busy = sending || voiceAnswering > 0;
  const sendingRef = useRef(false);
  sendingRef.current = busy;
  const [sendError, setSendError] = useState<string | null>(null);
  const [failedDraft, setFailedDraft] = useState<string | null>(null);
  // A follow-up chip whose send failed before anything arrived (TOM-709):
  // Retry sends it again as a chip, never through the box.
  const [failedFollowup, setFailedFollowup] = useState<{
    content: string;
    followup: NeoFollowupRef;
  } | null>(null);
  // The photos and files waiting in the chat box (TOM-671).
  const attachments = useChatAttachments();
  // Stops the running turn: the chat box's Stop (TOM-671). Null between turns.
  const stopRef = useRef<(() => void) | null>(null);
  // Server-owned conversation id for multi-turn history (ADR 0100). Null until
  // the first answered turn; reset when the boat context changes. The main
  // chat keeps it in this tab's sessionStorage, so a reload continues the same
  // conversation and the sitting is summarised once, not once per reload
  // (TOM-252). The transcript is read back from the server
  // (lib/neoChatHistory.ts), so the owner sees the conversation Neo continues.
  const [conversationId, setConversationId] = useState<string | null>(() =>
    routeChat
      ? routeConversationId
      : sessionKept
        ? loadNeoConversationId('chat', resumeScope)
        : null
  );
  // True while a continued conversation's messages are being read. Nothing is
  // sent meanwhile, so the read never lands on top of a new turn.
  const [hydrating, setHydrating] = useState(false);
  // Set when a chat opened by its address cannot be shown (TOM-195).
  const [openError, setOpenError] = useState<string | null>(null);
  // Bumped to start the chat again from the session record: "New chat", or a
  // panel that finds the record changed by the page while it was closed.
  const [restartKey, setRestartKey] = useState(0);
  // The one-time "Neo now remembers" notice (TOM-252), dismissed per account
  // in localStorage. Storage that cannot be read shows it until it is
  // dismissed on this page.
  const [recallNoticeDismissed, setRecallNoticeDismissed] = useState(
    () => userId === null || readNeoRecallNoticeDismissed(userId)
  );
  // Read by an alarm-change card at click time, so its note lands in the
  // conversation as it is then — the card outlives the turn that raised it.
  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conversationId;
  // Id of the assistant bubble currently being streamed (null between turns).
  // While sending with this still null, no token has arrived yet → show the
  // "thinking" indicator; once a bubble exists, the streamed text replaces it.
  const [streamingId, setStreamingId] = useState<string | null>(null);
  // True while a turn whose stream dropped is read back by its ref (TOM-712).
  const [recovering, setRecovering] = useState(false);
  // The specialists at work in the running turn, from its `agent` frames
  // (TOM-543): the busy row names the newest. Cleared when the turn ends.
  const [agentsAtWork, setAgentsAtWork] = useState<readonly NeoAgentAtWork[]>([]);
  // The receipt a turn sent from here made with board records, not one read
  // back from history: the chat page opens its board beside it (TOM-357).
  const [liveReceiptId, setLiveReceiptId] = useState<string | null>(null);
  // Aborts the in-flight streaming fetch on boat change / unmount.
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  // The question the owner just sent (TOM-709): the log follows its answer
  // down only until the question reaches the top, so the question and the
  // answer's conclusion stay in view while the rest streams in below.
  const holdQuestionRef = useRef<string | null>(null);
  // The log's last scroll position, to tell the owner scrolling up from the
  // log's own smooth scroll toward a taller bottom.
  const lastScrollTopRef = useRef(0);
  // The arrow down to the newest message (TOM-692): shown while the log no
  // longer follows new lines because the owner scrolled up.
  const [showJump, setShowJump] = useState(false);
  const contextRequestRef = useRef(0);
  // Bumped whenever the boat context changes; a chat send captures it and
  // ignores its own late response if the boat changed meanwhile (ADR 0105) —
  // otherwise a response for boat A could set conversationId / append a turn
  // into boat B's freshly-reset thread.
  const sendGenerationRef = useRef(0);
  // Each answer's feedback posts, chained in the order the owner made them
  // (TOM-461): the server keeps the last, so a 👎 landing after its reason
  // would erase the reason.
  const feedbackPostsRef = useRef(new Map<string, Promise<unknown>>());

  const context = contextState.kind === 'ready' ? contextState.context : null;

  useEffect(() => {
    if (routeConversationId !== undefined && routeConversationId === startedHereRef.current) {
      startedHereRef.current = null;
      return;
    }
    // Abandon any in-flight stream for the previous boat; the generation bump
    // also makes its late events no-op.
    abortRef.current?.abort();
    setContextState({ kind: 'idle' });
    setMessages([]);
    setSendError(null);
    setFailedDraft(null);
    setFailedFollowup(null);
    setOpenError(null);
    // A new or another conversation opens on its latest message.
    shouldStickToBottomRef.current = true;
    holdQuestionRef.current = null;
    lastScrollTopRef.current = 0;
    // A chat opened by its address continues that conversation, and the main
    // chat the one the server keeps for this owner and boat (TOM-517). The
    // chat with no boat continues the one kept in this tab's session record
    // when the tab reloaded mid-chat, else none; a kept conversation of
    // another account is cleared by the read (TOM-252). A new chat has none.
    const kept =
      routeConversationId ??
      (sessionKept && userId !== null
        ? loadNeoConversationId('chat', { userId, boatId: boatId ?? null })
        : null);
    setConversationId(kept);
    setHydrating(kept !== null || (main && userId !== null));
    setStreamingId(null);
    setAgentsAtWork([]);
    // A voice call's turns belong to the boat it was on; the call hangs up.
    voiceTurnsRef.current.clear();
    voiceQuestionsRef.current.clear();
    setVoiceAnswering(0);
    // Clear `sending` HERE: the aborted turn's finally is gated on the send
    // generation and won't run setSending(false) once we bump the generation
    // below, so the new boat would otherwise be stuck with a disabled composer.
    setSending(false);
    contextRequestRef.current += 1;
    sendGenerationRef.current += 1;
    if (kept === null && !(main && userId !== null)) return;

    // Read the continued conversation once and show it in place of the
    // greeting (TOM-252). One the chat cannot show is forgotten, and the next
    // message starts a new one: the owner never talks to a conversation they
    // cannot see. A boat change meanwhile bumps the generation and drops this.
    const generation = sendGenerationRef.current;
    void (async () => {
      let resumed = kept;
      if (resumed === null && main && boatId !== undefined) {
        // Without the server's main chat (offline, say), the first message
        // starts a separate conversation, as a chat did before TOM-517.
        resumed = await fetchMainNeoConversation(boatId);
        if (sendGenerationRef.current !== generation) return;
        setConversationId(resumed);
        if (resumed === null) {
          setHydrating(false);
          return;
        }
      }
      if (resumed === null) return;
      const read = await readNeoHistory(resumed, boatId);
      if (sendGenerationRef.current !== generation) return;
      const outcome = neoHistoryOutcome(read, resumed, boatId);
      if (outcome.kind === 'forget') {
        if (main && boatId !== undefined) {
          // The main chat was deleted from Chats: the server makes a new one.
          forgetMainNeoConversation(boatId);
          const again = await fetchMainNeoConversation(boatId);
          if (sendGenerationRef.current !== generation) return;
          setConversationId(again === resumed ? null : again);
        } else if (routeConversationId === undefined) {
          // Only the chat with no boat owns the session record.
          clearNeoConversationId('chat');
          setConversationId(null);
        } else {
          // A chat opened by its address says it cannot be shown.
          setOpenError(
            outcome.reason === 'gone'
              ? 'This chat is no longer available. A message here starts a new chat.'
              : 'Neo could not open this chat. A message here starts a new chat.'
          );
          setConversationId(null);
        }
      } else {
        // Neo's question about a panel, asked before the read landed, stays at the end.
        setMessages((prev) =>
          keepNeoPanelQuestionLast(prev, withNeoHistory<ChatMessage>(prev, outcome.messages))
        );
      }
      setHydrating(false);
    })();
    // `main` and `sessionKept` follow from the props listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boatId, userId, routeConversationId, fresh, restartKey]);

  // A message that reached the main chat from elsewhere (an alarm Neomaris
  // posted, a turn from another tab; TOM-517) is read in under what the chat
  // shows, and the log follows it down. Mid-turn, it waits for the turn to end.
  const [arrivals, setArrivals] = useState(0);
  const rereadAfterTurnRef = useRef(false);
  const rereadMain = useCallback(() => {
    if (!main || boatId === undefined) return;
    if (sendingRef.current) {
      rereadAfterTurnRef.current = true;
      return;
    }
    const generation = sendGenerationRef.current;
    void (async () => {
      const id = conversationIdRef.current ?? (await fetchMainNeoConversation(boatId));
      if (id === null || sendGenerationRef.current !== generation) return;
      const read = await readNeoHistory(id, boatId);
      if (sendGenerationRef.current !== generation || sendingRef.current) return;
      const outcome = neoHistoryOutcome(read, id, boatId);
      if (outcome.kind !== 'show') return;
      if (conversationIdRef.current === null) setConversationId(id);
      const prev = messagesRef.current;
      const next = keepNeoPanelQuestionLast(
        prev,
        withLaterNeoHistory<ChatMessage>(prev, outcome.messages)
      );
      // New by id: in a chat holding only its greeting, the message takes the
      // greeting's place, so the count alone would not change.
      const shown = new Set(prev.map((m) => m.id));
      if (!next.some((m) => !shown.has(m.id))) return;
      shouldStickToBottomRef.current = true;
      holdQuestionRef.current = null;
      setMessages(next);
      setArrivals((n) => n + 1);
    })();
  }, [main, boatId]);

  useEffect(() => {
    if (!main || boatId === undefined) return;
    function onMainMessage(e: Event) {
      const detail = (e as CustomEvent<NeoMainMessageDetail | undefined>).detail;
      if (detail?.boatId === boatId) rereadMain();
    }
    window.addEventListener(NEO_MAIN_MESSAGE_EVENT, onMainMessage);
    return () => window.removeEventListener(NEO_MAIN_MESSAGE_EVENT, onMainMessage);
  }, [main, boatId, rereadMain]);

  // Neo's greeting is on its way (TOM-920): the chat says so until a greeting
  // it did not hold when the ask went out arrives, or for a minute at most.
  const [greetingPending, setGreetingPending] = useState(false);
  const greetingsBefore = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!main || boatId === undefined) return;
    let timer: number | undefined;
    function onPending(e: Event) {
      const detail = (e as CustomEvent<NeoGreetingPendingDetail | undefined>).detail;
      if (detail === undefined || detail.boatId !== boatId) return;
      window.clearTimeout(timer);
      if (!detail.pending) {
        setGreetingPending(false);
        return;
      }
      greetingsBefore.current = new Set(
        messagesRef.current.filter((m) => m.greeting === true).map((m) => m.id)
      );
      setGreetingPending(true);
      timer = window.setTimeout(() => setGreetingPending(false), NEO_GREETING_WAIT_MS);
    }
    window.addEventListener(NEO_GREETING_PENDING_EVENT, onPending);
    return () => {
      window.removeEventListener(NEO_GREETING_PENDING_EVENT, onPending);
      window.clearTimeout(timer);
    };
  }, [main, boatId]);
  useEffect(() => {
    if (!greetingPending) return;
    if (messages.some((m) => m.greeting === true && !greetingsBefore.current.has(m.id))) {
      setGreetingPending(false);
    }
  }, [messages, greetingPending]);

  // An Undo of Neo's change wrote a line into the chat whose turn made it
  // (TOM-662): a receipt's Undo here, or the board page's beside the Edit with
  // Neo panel. This chat adds it once when it is that conversation; any other
  // shows the stored row when it is next opened.
  useEffect(() => {
    function onUndoNote(e: Event) {
      const note = (e as CustomEvent<NeoUndoChatNote | undefined>).detail;
      if (note === undefined) return;
      setMessages((prev) => {
        const line = undoChatNoteLine(prev, note, conversationIdRef.current);
        if (line === null) return prev;
        shouldStickToBottomRef.current = true;
        holdQuestionRef.current = null;
        return [...prev, line];
      });
    }
    window.addEventListener(NEO_UNDO_NOTE_EVENT, onUndoNote);
    return () => window.removeEventListener(NEO_UNDO_NOTE_EVENT, onUndoNote);
  }, []);

  // Abort a streaming turn if the component unmounts mid-stream.
  useEffect(() => () => abortRef.current?.abort(), []);

  // A main-chat message that arrived mid-turn is read in once the turn ends.
  useEffect(() => {
    if (busy || !rereadAfterTurnRef.current) return;
    rereadAfterTurnRef.current = false;
    rereadMain();
  }, [busy, rereadMain]);

  // Fire the auto-send once the composer is free. `sendMessage` is hoisted and
  // intentionally omitted from deps (new identity each render) — the dep change
  // re-runs this with the latest closure; the `sending` guard prevents doubles.
  useEffect(() => {
    if (pendingAutoSend === null || busy || hydrating) return;
    const prompt = pendingAutoSend;
    setPendingAutoSend(null);
    void sendMessage(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoSend, busy, hydrating]);

  // Neo's question about a panel, once nothing is arriving: never under an
  // answer still on its way (TOM-958).
  useEffect(() => {
    if (pendingPanelQuestion === null || busy || hydrating) return;
    const { title, context } = pendingPanelQuestion;
    setPendingPanelQuestion(null);
    shouldStickToBottomRef.current = true;
    holdQuestionRef.current = null;
    setMessages((prev) =>
      withNeoPanelQuestion<ChatMessage>(prev, {
        id: newId(),
        role: 'assistant',
        content: askNeoPanelQuestion(title),
        local: true,
        // The panel's title is the owner's words: printed as typed.
        plain: true,
        panelQuestion: context,
      })
    );
  }, [pendingPanelQuestion, busy, hydrating]);

  // The glide covers at most 15% of the log's height: a loaded history jumps
  // to just above its bottom first, so the chat reaches the composer at once
  // and the short glide still shows there is more above.
  const glideToBottom = useCallback((el: HTMLDivElement) => {
    const bottom = el.scrollHeight - el.clientHeight;
    const glide = el.clientHeight * 0.15;
    if (bottom - el.scrollTop > glide) {
      el.scrollTo({ top: bottom - glide });
      lastScrollTopRef.current = el.scrollTop;
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;
    // Scrolled up, a new line lands below the fold: the arrow says so even
    // when the owner had not scrolled far enough to show it yet.
    if (!shouldStickToBottomRef.current) {
      setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight >= STICK_DISTANCE);
      return;
    }
    // Just sent (TOM-709): down to the answer, but never past the question.
    // Once the answer is taller than what fits under the question, the log
    // holds the question at its top and stops following; the arrow takes the
    // owner down to the newest line.
    const held = questionTop(el, holdQuestionRef.current);
    if (held !== null && held < el.scrollHeight - el.clientHeight) {
      shouldStickToBottomRef.current = false;
      if (held > el.scrollTop + 1) el.scrollTo({ top: held, behavior: 'smooth' });
      setShowJump(
        el.scrollHeight - Math.max(el.scrollTop, held) - el.clientHeight >= STICK_DISTANCE
      );
      return;
    }
    setShowJump(false);
    glideToBottom(el);
  }, [messages, sending, glideToBottom]);

  // The chat opens on the latest message, not the top of a long continued
  // conversation (TOM-252): the log mounts scrolled to the top.
  useEffect(() => {
    if (!active) return;
    shouldStickToBottomRef.current = true;
    holdQuestionRef.current = null;
    setShowJump(false);
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [active]);

  useEffect(() => {
    if (!active || contextState.kind !== 'idle') return;
    const requestId = contextRequestRef.current + 1;
    contextRequestRef.current = requestId;
    setContextState({ kind: 'loading' });
    void (async () => {
      try {
        const loaded = await api<NeoContext>(contextUrl(boatId));
        if (contextRequestRef.current !== requestId) return;
        setContextState({ kind: 'ready', context: loaded });
        setMessages((prev) =>
          prev.length === 0
            ? [
                {
                  id: newId(),
                  role: 'assistant',
                  content: greetingFor(loaded, auth.user?.displayName),
                  local: true,
                },
              ]
            : prev
        );
      } catch (err) {
        if (contextRequestRef.current !== requestId) return;
        const e = err as {
          status?: number;
          body?: { message?: string };
          message?: string;
        };
        if (e.status === 401) void auth.refresh();
        setContextState({
          kind: 'error',
          message: friendlyNeoError(err, 'context'),
        });
      }
    })();
  }, [auth, boatId, contextState.kind, active]);

  /**
   * Send the box's message, or `contentOverride`. With `followup` it is a
   * follow-up chip (TOM-709): its label is the owner's message and the request
   * names the answer and chip it came from, and the box's draft and its photos
   * and files stay where they are, whatever happens to the turn.
   */
  async function sendMessage(contentOverride?: string, followup?: NeoFollowupRef) {
    const chip = followup !== undefined;
    const content = (contentOverride ?? draft).trim();
    // While a continued conversation is still being read, nothing is sent, so
    // the read can never replace or repeat a new turn (TOM-252).
    if (content.length === 0 || busy || hydrating) return;
    // A file still uploading, or one that failed, holds the message back; the
    // chat box says so on the file. A chip sends none of them.
    if (!chip && (attachments.uploading || attachments.items.some((i) => i.status === 'failed'))) {
      return;
    }
    const sentFiles = chip ? [] : attachments.ready;
    // A reply to Neo's question about a panel names the panel (TOM-958).
    const turnContext =
      requestContext ?? (chip ? null : neoPanelQuestionContext(messagesRef.current));
    if (!chip) {
      setDraft('');
      attachments.clear();
    }
    setSendError(null);
    setFailedDraft(null);
    setFailedFollowup(null);

    const userMessage: ChatMessage = {
      id: newId(),
      role: 'user',
      content,
      ...(sentFiles.length > 0 ? { attachments: sentFiles } : {}),
    };
    const assistantId = newId();
    setMessages((prev) => [...prev, userMessage]);
    // The log follows the answer down until the question reaches its top.
    shouldStickToBottomRef.current = true;
    holdQuestionRef.current = userMessage.id;
    setSending(true);
    // Capture the boat-context generation; ignore this response if the boat
    // changes mid-flight (ADR 0105).
    const generation = sendGenerationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    // Stop (TOM-671): the server's cancel once the turn has named itself, so
    // what the turn already changed is still reported on the stream (§12.3.2);
    // before that, closing the stream stops it, as leaving the page does.
    let stopped = false;
    // The turn as drawn: its bubble, its records, and what its events said
    // (components/neo/neoStreamTurn.ts, shared with a voice call's turns).
    const turn = newNeoStreamTurn(assistantId, boatId);
    const host: NeoStreamTurnHost = {
      setMessages,
      setStreamingId,
      setLiveReceiptId,
      setAgentsAtWork,
      refreshAccount: () => void auth.refresh(),
    };
    const showBubble = (next: string) => showNeoTurnBubble(turn, host, next);

    stopRef.current = () => {
      if (stopped) return;
      stopped = true;
      // A dropped answer being read back stops being read (TOM-712).
      recovery.abort();
      if (turn.turnRef === null) {
        controller.abort();
        return;
      }
      void api(`/v1/neo/turns/${encodeURIComponent(turn.turnRef)}/cancel`, {
        method: 'POST',
      }).catch(() => controller.abort());
    };

    // The stream dropped before its `done` (TOM-712, ADR 0264): read the turn
    // back by its ref until it ends. Done, its stored answer takes the place of
    // what arrived, with its charts, cards and record; true then. Failed,
    // stopped, not found, or still running after the time limit: false, and
    // what arrived stays with the dropped-connection line.
    // Its own controller: Stop aborts the read in flight and the wait.
    const recovery = new AbortController();
    const recoverTurn = async (ref: string): Promise<boolean> => {
      const until = Date.now() + NEO_TURN_RECOVERY_MS;
      const live = () =>
        !stopped &&
        !controller.signal.aborted &&
        !recovery.signal.aborted &&
        sendGenerationRef.current === generation;
      const quit = () => recovery.abort();
      controller.signal.addEventListener('abort', quit, { once: true });
      setRecovering(true);
      try {
        while (live()) {
          const step = neoTurnRecovery(await readNeoTurn(ref, recovery.signal), ref, boatId);
          if (!live()) return false;
          // Signed out, or not the owner's turn: the chat says so as a send does.
          if (step.kind === 'auth') {
            throw Object.assign(new Error(`neo turn read ${step.status}`), {
              status: step.status,
              streamed: turn.shown,
            });
          }
          if (step.kind === 'lost') {
            if (step.message !== undefined) lostMessage = step.message;
            return false;
          }
          if (step.kind === 'answer') {
            showBubble(step.content);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: step.content,
                      charts: step.charts.length > 0 ? step.charts : undefined,
                      cards: step.cards.length > 0 ? step.cards : undefined,
                      ...(step.answer === null ? {} : { answer: step.answer }),
                    }
                  : m
              )
            );
            turn.nextConversationId = step.conversationId ?? undefined;
            turn.nextMessageId = step.messageId;
            return true;
          }
          if (Date.now() >= until) return false;
          await pause(NEO_TURN_POLL_MS, recovery.signal);
        }
        return false;
      } finally {
        controller.signal.removeEventListener('abort', quit);
        if (sendGenerationRef.current === generation) setRecovering(false);
      }
    };
    // A failed turn's own words, from its read-back, for the error line.
    let lostMessage: string | null = null;

    // The account and boat this turn was sent for: the conversation it is
    // answered in is kept for them (TOM-252).
    const turnScope = resumeScope;
    const post = (resumeId: string | null) =>
      fetch('/v1/neo/chat/stream', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content }],
          ...(boatId === undefined ? {} : { boatId }),
          ...(resumeId === null ? {} : { conversationId: resumeId }),
          ...(boatId !== undefined && turnContext !== null ? { context: turnContext } : {}),
          ...(sentFiles.length > 0 ? { attachments: sentFiles.map((a) => a.attachmentId) } : {}),
          // A chip names the answer and the chip it came from (TOM-709).
          ...(followup === undefined ? {} : { followup }),
          capabilities: neoChatCapabilities(answers, boatShell),
        }),
        credentials: 'same-origin',
        signal: controller.signal,
      });
    // The owner stopped the turn (TOM-671). Nothing of it is stored (§12.3.2).
    // What Neo had written stays, marked as stopped; with nothing written yet,
    // the message goes back into the box to change or send again, as a failed
    // send does, unless the turn already raised a card the owner can act on.
    const endStopped = () => {
      if (turn.shown) {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, stopped: true } : m))
        );
        return;
      }
      if (turn.proposalBatch > 0) return;
      setMessages((prev) => prev.filter((message) => message.id !== userMessage.id));
      // A chip's label never goes into the box.
      if (chip) return;
      setDraft((current) => (current.trim().length === 0 ? content : current));
      attachments.restore(sentFiles);
    };

    // Pre-stream failures (auth/validation/budget/spend/scope) are plain JSON.
    const failureBody = async (r: Response) =>
      r.ok ? null : ((await r.json().catch(() => null)) as { message?: string } | null);

    try {
      let res = await post(conversationId);
      if (sendGenerationRef.current !== generation) return;
      let body = await failureBody(res);
      if (isNeoConversationGone(conversationId, res.status, body)) {
        // The server no longer knows the conversation this chat continued
        // (deleted since this tab kept it, say). The route refused before the
        // model was called, so forget it and ask in a new one (TOM-252): the
        // main chat in the one the server makes now (TOM-517), the chat with no
        // boat in a new one, its session record cleared.
        if (sessionKept) clearNeoConversationId('chat');
        let again: string | null = null;
        if (main && boatId !== undefined) {
          forgetMainNeoConversation(boatId);
          again = await fetchMainNeoConversation(boatId);
          if (sendGenerationRef.current !== generation) return;
          if (again === conversationId) again = null;
        }
        setConversationId(again);
        res = await post(again);
        if (sendGenerationRef.current !== generation) return;
        body = await failureBody(res);
      }
      if (!res.ok) {
        throw Object.assign(new Error(`neo stream ${res.status}`), {
          status: res.status,
          body,
        });
      }
      if (res.body === null) throw new Error('neo stream: no response body');

      const streamRead = readNeoStream(res.body, (event, data) => {
        // Boat changed (or this turn was aborted) mid-stream: a read() that had
        // already resolved with buffered frames still runs its continuation, so
        // guard HERE too — not just at the await boundaries — or a stale bubble
        // leaks into the new boat's freshly-cleared thread (ADR 0105).
        if (sendGenerationRef.current !== generation || controller.signal.aborted) return;
        applyNeoStreamEvent(turn, host, event, data);
      });
      // A link that drops mid-answer can also end the read with an error. A
      // turn that has named itself is read back below either way (TOM-712).
      let streamFailure: unknown = null;
      await streamRead.catch((err: unknown) => {
        streamFailure = err ?? new Error('neo stream failed');
      });
      if (sendGenerationRef.current !== generation) return;
      const namedTurn = turn.turnRef;
      if (streamFailure !== null && (namedTurn === null || stopped || controller.signal.aborted)) {
        throw streamFailure;
      }
      if (!turn.sawDone && turn.streamError === null && !stopped && namedTurn !== null) {
        if (await recoverTurn(namedTurn)) turn.sawDone = true;
        if (sendGenerationRef.current !== generation) return;
      }

      if (stopped || turn.streamError?.code === 'canceled') {
        endStopped();
        return;
      }
      if (turn.streamError !== null) {
        const e: NeoStreamEvent = turn.streamError;
        throw Object.assign(new Error(e.message ?? 'neo stream error'), {
          status: e.code === 'llm_timeout' ? 504 : e.code === 'forbidden' ? 403 : 502,
          body: { message: e.message },
          streamed: turn.shown,
        });
      }
      // Stream ended without a terminal `done`/`error` frame → the connection
      // dropped mid-answer. Surface it like a mid-stream error (a partial answer,
      // if any, is kept; otherwise the draft is restored and Retry enabled).
      if (!turn.sawDone) {
        // Read through a cast: the read-back above may have set it.
        const failedWith = lostMessage as string | null;
        throw Object.assign(new Error('neo stream ended without a terminal frame'), {
          status: 502,
          body: { message: failedWith ?? 'Neo’s connection dropped before it finished.' },
          streamed: turn.shown,
          // The turn's read-back said how it failed (TOM-712): those words, not ours.
          turnFailed: failedWith !== null,
        });
      }
      const nextConversationId = turn.nextConversationId;
      if (nextConversationId !== undefined && nextConversationId !== null) {
        adoptConversation(nextConversationId, turnScope);
      }
      // Attach the persisted message id to the streamed bubble so the owner can
      // leave thumbs feedback on it (ADR 0119).
      if (turn.nextMessageId !== undefined) {
        const persistedMessageId = turn.nextMessageId;
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, messageId: persistedMessageId } : m))
        );
      }
    } catch (err) {
      if (sendGenerationRef.current !== generation) return;
      if (stopped) {
        endStopped();
        return;
      }
      if (controller.signal.aborted) return;
      const e = err as {
        status?: number;
        body?: { message?: string };
        streamed?: boolean;
        turnFailed?: boolean;
      };
      if (e.status === 401) void auth.refresh();
      if (turn.shown) {
        // A partial answer is already on screen — keep it; no draft-restore, and
        // leave failedDraft null so Retry stays disabled (re-asking would dup).
        // Use copy that matches the visible state — NOT the generic "your message
        // was not sent" (it was sent, and a partial reply is rendered above).
        setSendError(
          e.turnFailed === true && e.body?.message !== undefined
            ? e.body.message
            : 'Neo’s connection dropped before it finished — the partial reply above is what arrived.'
        );
        return;
      }
      if (turn.proposalBatch > 0) {
        // The turn failed, but the server had already reported real alarm drafts
        // — ADR 0161 emits `alarm_proposal` on the error path precisely so the
        // owner is not robbed of them. The cards above are still valid and still
        // creatable, so keep them AND keep the user's own message with them:
        // dropping the bubble would leave orphaned cards, and restoring the
        // draft for a Retry would duplicate them.
        setSendError(
          'Neo’s turn ended early — the suggestion above is ready to create or dismiss.'
        );
        return;
      }
      // Nothing was shown yet — behave like the blocking path: drop the user
      // bubble, restore the draft, and enable Retry. A chip leaves the box
      // alone: Retry sends the chip again.
      setSendError(friendlyNeoError(err, 'chat'));
      setMessages((prev) => prev.filter((message) => message.id !== userMessage.id));
      if (followup !== undefined) {
        setFailedFollowup({ content, followup });
        return;
      }
      setFailedDraft(content);
      setDraft(content);
      attachments.restore(sentFiles);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      stopRef.current = null;
      if (sendGenerationRef.current === generation) {
        setSending(false);
        setStreamingId(null);
        // The turn is over, whether or not each specialist's end arrived.
        setAgentsAtWork([]);
      }
    }
  }

  /** A turn answered in `id`: the chat continues it from now on. */
  function adoptConversation(id: string, scope: typeof resumeScope) {
    const started = conversationIdRef.current === null;
    setConversationId(id);
    // Read at once by a voice call's next question, before the next render.
    conversationIdRef.current = id;
    // Only the chat with no boat keeps its conversation in the session
    // record. The main chat is the server's; a chat opened by its address
    // is found again by that address, and a new chat takes one now.
    if (sessionKept) saveNeoConversationId('chat', scope, id);
    if (fresh && started) {
      startedHereRef.current = id;
      onStartedRef.current?.(id);
    }
  }

  // A voice call's questions (TOM-731), by their event ids, oldest first:
  // each drawn by applyNeoStreamEvent exactly as a typed turn.
  const voiceTurnsRef = useRef(new Map<string, NeoStreamTurn>());
  // Each open question's own bubble, by the same event ids.
  const voiceQuestionsRef = useRef(new Map<string, string>());
  // Questions open while the call's stream dropped: events were missed, so
  // each is read back by its turn (TOM-712) when it ends.
  const voiceGappedRef = useRef(new Set<string>());

  /** A voice turn's stored answer, read back by its ref, in place of what arrived. */
  function showStoredVoiceAnswer(turn: NeoStreamTurn, step: NeoStoredAnswer) {
    showNeoTurnBubble(turn, voiceHost(), step.content);
    setMessages((prev) =>
      prev.map((m) =>
        m.id === turn.assistantId
          ? {
              ...m,
              content: step.content,
              charts: step.charts.length > 0 ? step.charts : undefined,
              cards: step.cards.length > 0 ? step.cards : undefined,
              ...(step.answer === null ? {} : { answer: step.answer }),
            }
          : m
      )
    );
    turn.sawDone = true;
    turn.nextConversationId = step.conversationId ?? undefined;
    turn.nextMessageId = step.messageId;
  }

  /**
   * One read of a voice question's turn by its ref (§12.3.1). Done, its stored
   * answer replaces what arrived and the question closes; failed or gone, it
   * closes as it stands; still running, its own events carry on.
   */
  async function readVoiceTurn(eventId: string, turn: NeoStreamTurn) {
    const ref = turn.turnRef;
    if (ref === null) return;
    const generation = sendGenerationRef.current;
    const step = neoTurnRecovery(await readNeoTurn(ref, new AbortController().signal), ref, boatId);
    if (sendGenerationRef.current !== generation) return;
    const open = voiceTurnsRef.current.get(eventId) === turn;
    if (step.kind === 'answer') {
      voiceGappedRef.current.delete(eventId);
      showStoredVoiceAnswer(turn, step);
      if (open) closeVoiceTurn(eventId, 'ended');
      else if (turn.nextMessageId !== undefined) {
        const persisted = turn.nextMessageId;
        setMessages((prev) =>
          prev.map((m) => (m.id === turn.assistantId ? { ...m, messageId: persisted } : m))
        );
      }
      return;
    }
    if (step.kind !== 'wait' && open) {
      voiceGappedRef.current.delete(eventId);
      closeVoiceTurn(eventId, 'interrupted');
    }
  }

  function voiceHost(): NeoStreamTurnHost {
    return {
      setMessages,
      setStreamingId,
      setLiveReceiptId,
      setAgentsAtWork,
      refreshAccount: () => void auth.refresh(),
    };
  }

  /** A voice turn ended (`done`, `error`, interrupted or the call's end). */
  function closeVoiceTurn(eventId: string, how: 'ended' | 'interrupted') {
    const turns = voiceTurnsRef.current;
    const turn = turns.get(eventId);
    if (turn === undefined) return;
    turns.delete(eventId);
    const questionId = voiceQuestionsRef.current.get(eventId);
    voiceQuestionsRef.current.delete(eventId);
    const { assistantId } = turn;
    if (turn.sawDone) {
      if (turn.nextConversationId !== undefined && turn.nextConversationId !== null) {
        adoptConversation(turn.nextConversationId, resumeScope);
      }
      const persisted = turn.nextMessageId;
      if (persisted !== undefined) {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, messageId: persisted } : m))
        );
      }
    } else if (turn.shown) {
      // Cut off part way: what arrived stays, marked as stopped.
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, stopped: true } : m)));
    } else if (how === 'interrupted' && turn.proposalBatch === 0) {
      // Talked over, or replaced by the next words, before Neo said anything:
      // the question goes, as a typed one with no answer leaves the log. Live,
      // ElevenLabs re-sends a sentence as it grows ("How are the engines
      // doing?", then "… Actually, never mind. How much fresh water is
      // left?"), and each would otherwise stay as its own bubble.
      if (questionId !== undefined) {
        setMessages((prev) => prev.filter((m) => m.id !== questionId));
      }
    } else if (
      how === 'ended' &&
      turn.streamError !== null &&
      turn.streamError.code !== 'canceled'
    ) {
      // Neo could not answer: the call said so aloud; the chat says so too.
      const said = turn.streamError.message;
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'assistant',
          content:
            typeof said === 'string' && said.length > 0 ? said : 'Neo could not answer that.',
          local: true,
        },
      ]);
    }
    if (turns.size === 0) {
      setStreamingId(null);
      setAgentsAtWork([]);
    }
    setVoiceAnswering(turns.size);
  }

  const voiceRef = useRef<NeoVoiceTurns | null>(null);
  voiceRef.current = {
    conversationId: () => conversationIdRef.current,
    heard(eventId, text) {
      if (voiceTurnsRef.current.has(eventId)) return;
      const question: ChatMessage = { id: newId(), role: 'user', content: text };
      setMessages((prev) => [...prev, question]);
      // The log follows the answer down until the question reaches its top.
      shouldStickToBottomRef.current = true;
      holdQuestionRef.current = question.id;
      voiceTurnsRef.current.set(eventId, newNeoStreamTurn(newId(), boatId));
      voiceQuestionsRef.current.set(eventId, question.id);
      setVoiceAnswering(voiceTurnsRef.current.size);
    },
    event(eventId, event, data) {
      const turns = voiceTurnsRef.current;
      // Without an id, the newest question's. With one this chat no longer
      // holds, nothing: a talked-over turn's late `error canceled` comes after
      // its `voice_interrupted`, and must not end the question after it.
      const id = eventId === null ? [...turns.keys()].at(-1) : eventId;
      if (id === undefined || !turns.has(id)) return;
      const turn = turns.get(id)!;
      applyNeoStreamEvent(turn, voiceHost(), event, data);
      if (event !== 'done' && event !== 'error') return;
      closeVoiceTurn(id, 'ended');
      // Some of its words were missed while the stream was down: the stored
      // answer takes their place.
      if (event === 'done' && voiceGappedRef.current.delete(id)) void readVoiceTurn(id, turn);
    },
    resync() {
      for (const [id, turn] of voiceTurnsRef.current) {
        voiceGappedRef.current.add(id);
        void readVoiceTurn(id, turn);
      }
    },
    interrupted(eventId) {
      closeVoiceTurn(eventId, 'interrupted');
    },
    ended() {
      for (const id of [...voiceTurnsRef.current.keys()]) closeVoiceTurn(id, 'interrupted');
      voiceGappedRef.current.clear();
      // What the call's turns stored after its stream closed is read in.
      if (main) rereadMain();
    },
  };
  // One object for the call's whole life, always reaching this render's chat.
  const [voiceTurns] = useState<NeoVoiceTurns>(() => ({
    conversationId: () => voiceRef.current!.conversationId(),
    heard: (eventId, text) => voiceRef.current!.heard(eventId, text),
    event: (eventId, event, data) => voiceRef.current!.event(eventId, event, data),
    interrupted: (eventId) => voiceRef.current!.interrupted(eventId),
    resync: () => voiceRef.current!.resync(),
    ended: () => voiceRef.current!.ended(),
  }));

  async function sendFeedback(message: ChatMessage, rating: FeedbackRating) {
    if (message.messageId === undefined) return;
    // Set-only: clicking the already-selected rating is a no-op, so the UI never
    // shows a state the server doesn't hold. There is no un-rate (the API upserts,
    // it has no clear) — the user can still switch 👍↔👎 (Codex-review fix:
    // previously a re-click cleared the highlight locally while the row remained).
    if (message.feedback === rating) return;
    // A 👎 also opens the ask for what was wrong; a 👍 closes it (TOM-461).
    const feedbackAsk = neoFeedbackAskAfterRating(message.feedbackAsk, rating);
    setMessages((prev) =>
      prev.map((m) => (m.id === message.id ? { ...m, feedback: rating, feedbackAsk } : m))
    );
    try {
      await postFeedback(message.messageId, { rating });
    } catch {
      // Revert the optimistic change on failure.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? { ...m, feedback: message.feedback, feedbackAsk: message.feedbackAsk }
            : m
        )
      );
    }
  }

  /**
   * The owner's answer to the ask under a 👎 (TOM-461): the same rating again,
   * with the reason and the words. True once the server holds it.
   */
  async function sendFeedbackDetail(
    message: ChatMessage,
    detail: NeoFeedbackDetail
  ): Promise<boolean> {
    if (message.messageId === undefined) return false;
    try {
      await postFeedback(message.messageId, { rating: 'down', ...detail });
    } catch {
      return false;
    }
    setMessages((prev) =>
      prev.map((m) =>
        m.id === message.id && m.feedback === 'down' && m.feedbackAsk === 'open'
          ? { ...m, feedbackAsk: 'thanked' }
          : m
      )
    );
    return true;
  }

  /** Closed without sending: the 👎 stands alone. */
  function closeFeedbackAsk(message: ChatMessage) {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === message.id && m.feedbackAsk === 'open' ? { ...m, feedbackAsk: undefined } : m
      )
    );
  }

  function postFeedback(messageId: string, body: { rating: FeedbackRating } & NeoFeedbackDetail) {
    const before = feedbackPostsRef.current.get(messageId) ?? Promise.resolve();
    const post = before.then(() =>
      api(`/v1/neo/messages/${messageId}/feedback`, { method: 'POST', body })
    );
    // The chain waits on a failed post too, then goes on.
    feedbackPostsRef.current.set(
      messageId,
      post.catch(() => undefined)
    );
    return post;
  }

  function confirmedLabel(change: NeoLabelChange) {
    if (boatId !== undefined && boatId !== change.boatId) return;
    setMessages((previous) => {
      const next = previous.map((message) =>
        message.labelRequests === undefined
          ? message
          : {
              ...message,
              labelRequests: message.labelRequests.map((request) =>
                request.requestId === change.requestId
                  ? { ...request, status: 'applied' as const, change }
                  : request
              ),
            }
      );
      if (
        next.some((message) =>
          message.receipt?.labels?.some((label) => label.requestId === change.requestId)
        )
      )
        return next;
      return [
        ...next,
        {
          id: `${change.turnRef}-label-receipt-${change.requestId}`,
          role: 'assistant' as const,
          content: '',
          local: true,
          receipt: { boards: [], labels: [change] },
        },
      ];
    });
  }

  function updateDraft(value: string) {
    setDraft(value);
    if (sendError !== null) setSendError(null);
    if (failedDraft !== null) setFailedDraft(null);
    if (failedFollowup !== null) setFailedFollowup(null);
  }

  /** The send error's Retry: the failed message again, or the failed chip. */
  function retry() {
    if (failedFollowup !== null) {
      void sendMessage(failedFollowup.content, failedFollowup.followup);
      return;
    }
    if (failedDraft !== null) void sendMessage(failedDraft);
  }

  /** A follow-up chip (TOM-709): its label, sent as the owner's message. */
  function sendFollowup(label: string, followup: NeoFollowupRef) {
    void sendMessage(label, followup);
  }

  /** A prompt handed over by a page (`nm:open-neo`): put in the composer, or sent. */
  function seed(prompt: string, autoSend: boolean) {
    if (autoSend) setPendingAutoSend(prompt);
    else setDraft(prompt);
  }

  /**
   * Ask Neo on a panel (TOM-958): Neo asks what the owner would like to
   * change about it, at the end of the thread, and the owner's reply names the
   * panel (lib/neoPanelQuestion). Nothing is sent until they reply.
   */
  function askAboutPanel(title: string, context: NeoRequestContext) {
    setPendingPanelQuestion({ title, context });
  }

  /**
   * Start a new conversation. On a boat it is a separate chat at `neo/new`,
   * and the main chat stays as it is (TOM-517, ADR 0337 §2). With no boat the
   * session record is cleared and the chat starts again with the greeting; the
   * conversation it held is kept on the server and listed on Chats (TOM-195).
   */
  function startNewChat() {
    if (boatId !== undefined && !fresh) {
      navigate(newNeoChatPath(boatId));
      return;
    }
    if (routeChat) return;
    if (sessionKept) clearNeoConversationId('chat');
    setDraft('');
    setRestartKey((key) => key + 1);
  }

  /**
   * The panel stays mounted while another host continues the conversation.
   * When it opens, the main chat reads in what arrived meanwhile (TOM-517),
   * and the chat with no boat picks up the conversation the session record
   * now holds, so its next answer does not write an older id back over it.
   */
  function syncWithSessionRecord() {
    if (main) {
      rereadMain();
      return;
    }
    if (!sessionKept || sending || resumeScope === null) return;
    if (loadNeoConversationId('chat', resumeScope) !== conversationIdRef.current) {
      setRestartKey((key) => key + 1);
    }
  }

  function dismissRecallNotice() {
    setRecallNoticeDismissed(true);
    if (userId !== null) dismissNeoRecallNotice(userId);
  }

  function onMessagesScroll() {
    const el = scrollRef.current;
    if (el === null) return;
    // The owner scrolled on past the question the log holds at its top: from
    // here the log follows the bottom as before (TOM-709). The log's own
    // scroll never goes past the question.
    const held = questionTop(el, holdQuestionRef.current);
    if (held !== null && el.scrollTop > held + 4) holdQuestionRef.current = null;
    // Near the bottom, the log follows new lines again. It stops following
    // only when the owner scrolls up. The log's own smooth scroll moves only
    // down, so a chart or a long line that moves the bottom far at once does
    // not stop it.
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance < STICK_DISTANCE) shouldStickToBottomRef.current = true;
    else if (el.scrollTop < lastScrollTopRef.current) shouldStickToBottomRef.current = false;
    lastScrollTopRef.current = el.scrollTop;
    setShowJump(!shouldStickToBottomRef.current);
  }

  /** The arrow: back down to the newest message, following new lines again. */
  function jumpToBottom() {
    const el = scrollRef.current;
    if (el === null) return;
    shouldStickToBottomRef.current = true;
    holdQuestionRef.current = null;
    setShowJump(false);
    glideToBottom(el);
  }

  return {
    boatId,
    contextState,
    context,
    retryContext: () => setContextState({ kind: 'idle' }),
    messages,
    draft,
    updateDraft,
    confirmedLabel,
    seed,
    askAboutPanel,
    requestContext,
    setRequestContext,
    /** A turn is being answered: a typed one, or a voice call's question. */
    sending: busy,
    /** A typed turn is running, which Stop can stop. */
    stoppable: sending,
    /** Where a voice call draws its turns (TOM-731). */
    voiceTurns,
    /** How many of a voice call's questions are being answered. */
    voiceAnswering,
    /** The capabilities this chat's turns offer, for a voice call's too. */
    capabilities: neoChatCapabilities(answers, boatShell),
    hydrating,
    sendError,
    /** True when the send error's Retry has something to send again. */
    canRetry: failedDraft !== null || failedFollowup !== null,
    retry,
    openError,
    streamingId,
    /** "Neo is checking your boards…" while a specialist works (TOM-543); else null. */
    agentBusyLine: neoAgentBusyLine(agentsAtWork),
    /** True while a dropped answer is read back (TOM-712). */
    recovering,
    liveReceiptId,
    conversationIdRef,
    showRecallNotice:
      context !== null && showNeoRecallNotice(context.recallEnabled, recallNoticeDismissed),
    dismissRecallNotice,
    sendMessage,
    sendFollowup,
    /** This chat asks for each answer's record and shows the answer layout (TOM-709). */
    answers,
    /** Stop the running turn (TOM-671). */
    stop: () => stopRef.current?.(),
    attachments,
    sendFeedback,
    sendFeedbackDetail,
    closeFeedbackAsk,
    startNewChat,
    syncWithSessionRecord,
    /** Bumped each time a message reaches the main chat from elsewhere (TOM-517). */
    arrivals,
    /** Neo's greeting is queued and has not arrived yet (TOM-920). */
    greetingPending,
    scrollRef,
    inputRef,
    onMessagesScroll,
    showJump,
    jumpToBottom,
  };
}

type NeoConversation = ReturnType<typeof useNeoConversation>;

/** The composer's hint: the boat by name once the context names one. */
function composerPlaceholder(context: NeoContext | null): string {
  if (context !== null && context.boats.length === 0) return 'Ask a sailing question…';
  const boat =
    context !== null && context.boats.length === 1 ? context.boats[0]!.displayName : null;
  return `Ask Neo about ${boat ?? 'your boat'}…`;
}

/**
 * The conversation itself: loading and error lines, the recall notice, the
 * message log and the send error. Shared by the panel and the page.
 */
function NeoThread({
  chat,
  answerHooks,
  sources,
  onRecallLinkFollowed,
  onRecallNoticeDismissed,
  onOpenBoard,
  noticesInLog = false,
  noticesInThread = false,
  edit,
}: {
  chat: NeoConversation;
  /** The page keeps its notices at the top of the scrolling log, under the shell's header. */
  noticesInLog?: boolean;
  /**
   * The panel's notices are the thread's first item and scroll with it: held
   * above the log, the messages scrolled under them on a hard cut (TOM-546).
   */
  noticesInThread?: boolean;
  /**
   * What a follow-up chip that is not a question does (TOM-709): switch a
   * chart's view (W3), open Behind the answer (W2). A chat without them draws
   * no "Show the evidence" chip.
   */
  answerHooks?: NeoAnswerViewHooks;
  /**
   * Behind the answer (TOM-710), where it exists: whether an answer has a
   * record worth opening, the answer it shows now, and opening it.
   */
  sources?: {
    selected: string | null;
    has: (message: ChatMessage) => boolean;
    open: (answerId: string) => void;
  };
  onRecallLinkFollowed?: () => void;
  onRecallNoticeDismissed?: () => void;
  /** A receipt's Open board was followed. */
  onOpenBoard?: () => void;
  /**
   * Opened by Edit with Neo (TOM-356): the main chat's earlier messages show
   * faded above this panel's requests; this panel's receipts end in "Undo
   * changes" and "Done"; the wait and a failure name what is being changed.
   */
  edit?: {
    /** Messages before this index came before the panel was opened for editing. */
    earlierCount: number;
    loading: string;
    error: string;
    onDone: () => void;
  };
}) {
  // The boat the chat is on, for the links its messages carry.
  const { boatId } = useParams();
  const { contextState, hydrating, messages, streamingId, sending, sendError } = chat;
  // What Neo is checking while a specialist works this turn (TOM-543).
  const checking = sending ? chat.agentBusyLine : null;
  // The boat a boat page's chat is scoped to, by name, for the receipts' scope lines.
  const boats = chat.context?.boats ?? [];
  const boatName = (boatId: string) =>
    boatId === chat.boatId && boats.length === 1 ? boats[0]!.displayName : null;
  // Each receipt's parts, and the parts a later request changed again: those
  // offer no Undo, because a board keeps only the last request's step.
  const receiptMessages = messages.filter((m) => m.receipt !== undefined);
  const receiptParts = receiptMessages.map((m) =>
    buildReceipt(
      m.receipt!.boards,
      m.receipt!.answers ?? [],
      m.receipt!.labels ?? [],
      m.receipt!.notebook ?? []
    )
  );
  const supersededFor = new Map(
    supersededParts(receiptParts).map((keys, i) => [receiptMessages[i]!.id, keys])
  );
  const partsFor = new Map(receiptParts.map((parts, i) => [receiptMessages[i]!.id, parts]));
  // The owner's own looks keep one Undo step per boat (ADR 0364 §6): a later
  // request's private receipt for the boat supersedes an earlier one's.
  const looksMessages = messages.filter((m) => m.lookChanges !== undefined);
  const looksParts = looksMessages.map((m) => buildLooksReceipt(m.lookChanges!));
  const looksPartsFor = new Map(looksParts.map((parts, i) => [looksMessages[i]!.id, parts]));
  const looksSupersededFor = new Map(
    supersededLooksParts(looksParts).map((keys, i) => [looksMessages[i]!.id, keys])
  );
  // A request that changed both what the owners share and the owner's own
  // looks has one Undo for the whole of it, on either receipt (TOM-595), and
  // so does one that changed the boat's notebook, whose notes have no route of
  // their own (TOM-611).
  const wholeTurnUndo = useWholeTurnUndos();
  const wholeTurnRefs = new Set([
    ...wholeTurnUndoRefs(receiptParts, looksParts),
    ...notebookTurnRefs(receiptParts),
  ]);
  const sharedOfTurn = new Map<string, ReceiptPart[]>();
  const looksOfTurn = new Map<string, LooksReceiptPart[]>();
  for (const parts of receiptParts) {
    const ref = receiptTurnRef(parts);
    if (ref !== null && wholeTurnRefs.has(ref))
      sharedOfTurn.set(ref, [...(sharedOfTurn.get(ref) ?? []), ...parts]);
  }
  for (const parts of looksParts) {
    const ref = looksTurnRef(parts);
    if (ref !== null && wholeTurnRefs.has(ref))
      looksOfTurn.set(ref, [...(looksOfTurn.get(ref) ?? []), ...parts]);
  }
  const wholeTurnOf = (ref: string | null) =>
    ref !== null && wholeTurnRefs.has(ref)
      ? wholeTurnUndo(ref, sharedOfTurn.get(ref) ?? [], looksOfTurn.get(ref) ?? [])
      : undefined;
  const notices = (
    <>
      {contextState.kind === 'loading' || hydrating ? (
        <div className="neo-status-line" role="status" aria-live="polite">
          <span className="sr-only">
            {hydrating ? 'Loading this conversation' : 'Loading boat context'}
          </span>
        </div>
      ) : null}
      {contextState.kind === 'error' ? (
        <div className="neo-context-error" aria-live="polite">
          <span>{contextState.message}</span>
          <button type="button" className="neo-inline-button" onClick={chat.retryContext}>
            Retry
          </button>
        </div>
      ) : null}
      {chat.openError !== null ? (
        <div className="neo-context-error" aria-live="polite" data-testid="neo-open-error">
          <span>{chat.openError}</span>
          {chat.boatId !== undefined ? (
            <Link className="neo-inline-button" to={`/dashboard/boats/${chat.boatId}/chats`}>
              All chats
            </Link>
          ) : null}
        </div>
      ) : null}
      {chat.showRecallNotice ? (
        // Told once, while the owner's summaries are on (TOM-252). Following
        // the link counts as seeing it.
        <div className="neo-recall-notice" data-testid="neo-recall-notice">
          <p>
            {NEO_RECALL_NOTICE}{' '}
            <Link
              to="/profile"
              onClick={() => {
                chat.dismissRecallNotice();
                onRecallLinkFollowed?.();
              }}
            >
              {NEO_RECALL_NOTICE_LINK}
            </Link>
          </p>
          <button
            type="button"
            className="neo-inline-button"
            onClick={() => {
              chat.dismissRecallNotice();
              onRecallNoticeDismissed?.();
            }}
          >
            Got it
          </button>
        </div>
      ) : null}
    </>
  );
  const hasNotice =
    contextState.kind === 'loading' ||
    contextState.kind === 'error' ||
    hydrating ||
    chat.openError !== null ||
    chat.showRecallNotice;
  // The answer layout (TOM-709): on a chat that asks for answers, never in
  // the board's Edit chat.
  const layout = chat.answers && edit === undefined;
  const groups = layout ? groupNeoAnswers(messages) : [];
  const answerIds = new Set(
    groups.flatMap((group) => (group.answer === null ? [] : [group.answer.id]))
  );
  const renderMessage = (message: (typeof messages)[number], inPanel: boolean) => {
    if (message.labelRequests !== undefined) {
      return (
        <div key={message.id} className="neo-proposals">
          {message.labelRequests.map((request) => (
            <NeoLabelChangeCard
              key={request.requestId}
              request={request}
              onConfirmed={chat.confirmedLabel}
            />
          ))}
        </div>
      );
    }
    if (message.changeRequests !== undefined) {
      return (
        <div key={message.id} className="neo-proposals" data-testid="neo-alarm-change-requests">
          <span className="sr-only">Neo asks you to confirm:</span>
          {message.changeRequests.map((request) => (
            <NeoAlarmChangeCard
              key={request.clientId}
              request={request}
              conversationId={() => chat.conversationIdRef.current}
            />
          ))}
        </div>
      );
    }
    if (message.proposals !== undefined) {
      // Confirmation cards are an action surface, not a bubble — they
      // render outside the message chrome so the card owns its own frame.
      return (
        <div key={message.id} className="neo-proposals" data-testid="neo-alarm-proposals">
          <span className="sr-only">Neo suggests:</span>
          {message.proposals.map((proposal) => (
            <NeoAlarmProposalCard key={proposal.clientId} proposal={proposal} />
          ))}
        </div>
      );
    }
    if (message.receipt !== undefined) {
      const parts = partsFor.get(message.id) ?? [];
      return (
        <NeoReceipt
          key={message.id}
          parts={parts}
          boatName={boatName}
          onOpenBoard={onOpenBoard}
          superseded={supersededFor.get(message.id)}
          panel={inPanel && edit !== undefined ? { onDone: edit.onDone } : undefined}
          wholeTurn={wholeTurnOf(receiptTurnRef(parts))}
        />
      );
    }
    if (message.lookChanges !== undefined) {
      const parts = looksPartsFor.get(message.id) ?? [];
      return (
        <NeoLooksReceipt
          key={message.id}
          parts={parts}
          boatName={boatName}
          superseded={looksSupersededFor.get(message.id)}
          wholeTurn={wholeTurnOf(looksTurnRef(parts))}
        />
      );
    }
    if (isNoteMessage(message)) {
      // An undo note is what someone did, not Neo speaking (TOM-662).
      return message.undoNote === true ? (
        <div key={message.id} className="neo-message neo-message--assistant neo-message--note">
          <Icon name="undo" size={15} />
          <span>{message.content}</span>
        </div>
      ) : (
        <div key={message.id} className="neo-message neo-message--assistant neo-message--note">
          <span className="sr-only">Neo:</span>
          <CheckIcon />
          <span>{message.content.replace(NOTE_PREFIX, '')}</span>
        </div>
      );
    }
    const hasChart =
      message.role === 'assistant' &&
      (HAS_CHART_MARKER.test(message.content) ||
        answerShowsCard(message.content, message.cards, message.id === streamingId));
    const streaming = message.id === streamingId;
    // Neo's answer on a chat that shows answers (TOM-709): "✦ Neo", the
    // conclusion, the brief source line, then the rest of the answer.
    const framed = layout && message.role === 'assistant' && answerIds.has(message.id);
    const parts = framed ? neoConclusion(message.content, streaming) : null;
    const source = framed ? neoSourceLine(message.answer?.source, Date.now()) : null;
    // Neo's question about a panel (TOM-958): "✦ Neo" over it, as over an
    // answer, with nothing else of an answer's frame.
    const asksAboutPanel = message.panelQuestion !== undefined;
    const byline = framed || (layout && asksAboutPanel);
    return (
      <div
        key={message.id}
        className={`neo-message neo-message--${message.role}${hasChart ? ' neo-message--has-chart' : ''}${byline ? ' neo-answer' : ''}`}
        data-neo-message={message.id}
        {...(framed
          ? { 'data-testid': 'neo-answer' }
          : asksAboutPanel
            ? { 'data-testid': 'neo-panel-question' }
            : {})}
      >
        <span className="sr-only">{message.role === 'assistant' ? 'Neo' : 'You'}:</span>
        {byline ? <NeoAnswerByline /> : null}
        {parts?.conclusion != null ? (
          <div className="neo-answer__conclusion" data-testid="neo-answer-conclusion">
            <NeoMarkdown content={`## ${parts.conclusion}`} />
          </div>
        ) : null}
        {source !== null ? <NeoAnswerSourceLine text={source} /> : null}
        {message.attachments !== undefined && message.attachments.length > 0 ? (
          <MessageAttachments attachments={message.attachments} />
        ) : null}
        {parts === null || parts.body.length > 0 ? (
          <NeoMessageBody message={message} streaming={streaming} content={parts?.body} />
        ) : null}
        {message.stopped === true ? (
          <p className="neo-stopped" data-testid="neo-stopped">
            Stopped
          </p>
        ) : null}
        {message.alarm !== undefined && boatId !== undefined ? (
          // Neomaris's message about an alarm opens that alarm (TOM-551).
          <Link
            className="neo-inline-button neo-alarm-link"
            to={boatPath(boatId, `alarms/${encodeURIComponent(message.alarm.alertId)}`)}
            data-testid="neo-alarm-link"
          >
            Review alarm
          </Link>
        ) : null}
        {framed && !streaming ? (
          // Copy and Helpful, small and secondary (TOM-709).
          <NeoAnswerActions
            copyText={() => neoAnswerCopyText(message.content, message.charts, message.cards)}
            sources={
              sources !== undefined && sources.has(message)
                ? {
                    open: sources.selected === message.id,
                    onOpen: () => sources.open(message.id),
                  }
                : undefined
            }
          >
            {feedbackFor(message)}
          </NeoAnswerActions>
        ) : framed ? null : (
          feedbackFor(message)
        )}
      </div>
    );
  };
  function feedbackFor(message: (typeof messages)[number]) {
    if (message.role !== 'assistant' || message.messageId === undefined) return null;
    // Neo's greeting was not asked for: nothing to rate (TOM-920).
    if (message.greeting === true) return null;
    return (
      <NeoFeedback
        rating={message.feedback}
        ask={message.feedbackAsk}
        onRate={(rating) => void chat.sendFeedback(message, rating)}
        onSendDetail={(detail) => chat.sendFeedbackDetail(message, detail)}
        onCloseAsk={() => chat.closeFeedbackAsk(message)}
      />
    );
  }
  // Opened by Edit with Neo, the panel continues the main chat (boards-v3.md,
  // D4) but shows only its own requests and their answers, as the app's sheet
  // does: an earlier answer under this board's title read as the reply to a
  // request it never saw (TOM-426). A line says what the panel continues.
  const continuesChat = edit !== undefined && edit.earlierCount > 0;
  const currentMessages = edit !== undefined ? messages.slice(edit.earlierCount) : messages;
  // Each question with Neo's answer under it, and the follow-ups under the
  // newest answer only (TOM-709). Only a chat that asks for answers draws them.
  const chips = layout ? neoAnswerChips(groups, { streamingId, sending }) : null;
  const thread = layout
    ? groups.map((group, i) => (
        <Fragment key={group.question?.id ?? group.entries[0]?.id ?? i}>
          {group.question !== null ? renderMessage(group.question, false) : null}
          {group.entries.map((entry) => renderMessage(entry, false))}
          {chips !== null && chips.answerId === group.answer?.id ? (
            <NeoFollowups
              followups={chips.followups}
              disabled={sending || hydrating}
              onSend={(label, index) =>
                chat.sendFollowup(label, { messageId: chips.messageId, index })
              }
              onShowView={
                answerHooks?.showView === undefined
                  ? undefined
                  : (groupId, recordId) => answerHooks.showView!(chips.answerId, groupId, recordId)
              }
              onInspect={
                answerHooks?.inspectEvidence === undefined
                  ? undefined
                  : (evidenceId) => answerHooks.inspectEvidence!(chips.answerId, evidenceId)
              }
            />
          ) : null}
        </Fragment>
      ))
    : currentMessages.map((message) => renderMessage(message, edit !== undefined));
  return (
    <>
      {noticesInLog || noticesInThread ? null : notices}

      <div className="neo-log">
        <div
          className="neo-messages"
          ref={chat.scrollRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          // While a reply streams, mark the log busy so screen readers don't
          // announce every per-token text mutation; when it clears (turn done),
          // the completed answer is announced once instead of dozens of times.
          aria-busy={streamingId !== null}
          onScroll={chat.onMessagesScroll}
        >
          {noticesInLog && hasNotice ? <div className="neo-page-notices">{notices}</div> : null}
          {noticesInThread && hasNotice ? (
            <div className="neo-thread-notices">{notices}</div>
          ) : null}
          {continuesChat ? (
            <div className="neo-earlier">
              <p className="neo-earlier__label">Continues your main chat with Neo</p>
            </div>
          ) : null}
          {thread}
          {sending && (streamingId === null || checking !== null) ? (
            // While a specialist works (TOM-543) the row says what Neo is
            // checking, even under words Neo streamed before it asked.
            <div
              className="neo-message neo-message--assistant neo-message--busy"
              role="status"
              data-testid="neo-busy"
            >
              {checking === null && edit === undefined ? (
                <span className="sr-only">Neo is thinking</span>
              ) : null}
              <span className="neo-typing-dot" aria-hidden="true" />
              <span className="neo-typing-dot" aria-hidden="true" />
              <span className="neo-typing-dot" aria-hidden="true" />
              {checking !== null ? (
                <span className="neo-busy-text">{checking}</span>
              ) : edit !== undefined ? (
                <span className="neo-busy-text">{edit.loading}</span>
              ) : null}
            </div>
          ) : null}
          {chat.recovering ? (
            // The stream dropped; the answer is read back by its turn (TOM-712).
            <div className="neo-status-line" role="status" data-testid="neo-recovering">
              The connection dropped. Getting Neo’s answer…
            </div>
          ) : null}
        </div>
        {/* Outside the log, so screen readers don't announce it as a new line. */}
        <button
          type="button"
          className={`tb-icon-btn tb-icon-btn--glass neo-jump${chat.showJump ? ' neo-jump--shown' : ''}`}
          aria-label="Scroll to the newest message"
          title="Newest message"
          data-testid="neo-jump"
          onClick={chat.jumpToBottom}
        >
          <Icon name="arrow-down" size={18} />
        </button>
      </div>

      {sendError !== null ? (
        <div className="neo-send-error" aria-live="polite">
          <span>{edit !== undefined ? edit.error : sendError}</span>
          <button
            type="button"
            className="neo-inline-button"
            onClick={chat.retry}
            disabled={sending || !chat.canRetry}
          >
            {edit !== undefined ? 'Try again' : 'Retry'}
          </button>
        </div>
      ) : null}
    </>
  );
}

/**
 * Neo's chat box (TOM-671): the one chat box, with the + menu for photos and
 * files (and "New chat" where `onNewChat` is given), the mic, and Stop while
 * Neo answers.
 */
function NeoComposer({
  chat,
  describedBy,
  placeholder,
  onNewChat,
  voice,
  suggestions,
}: {
  chat: NeoConversation;
  describedBy?: string;
  /** The Edit with Neo panel names what a request will change ("Ask Neo to change Trip home"). */
  placeholder?: string;
  onNewChat?: () => void;
  /** A chat that offers a voice call (TOM-731): Talk, and the call's strip. */
  voice?: NeoVoice;
  /** The mode's suggested questions, in a row above the box (TOM-741). */
  suggestions?: readonly string[];
}) {
  const inCall = voice !== undefined && voice.phase !== 'idle';
  const suggestionRow =
    suggestions === undefined || inCall ? null : (
      <NeoSuggestions
        questions={suggestions}
        className="neo-suggestions"
        itemClassName="neo-suggestions__item"
        testId="neo-suggestion"
        onPick={(question) => {
          // Fills the box; the owner sends it.
          chat.updateDraft(question);
          chat.inputRef.current?.focus();
        }}
      />
    );
  const menuItems: ChatComposerMenuItem[] =
    onNewChat === undefined
      ? []
      : [
          {
            key: 'new-chat',
            label: 'New chat',
            icon: <NewChatIcon />,
            onSelect: onNewChat,
            testId: 'neo-new-chat',
          },
        ];
  return (
    <ChatComposer
      className="neo-composer"
      value={chat.draft}
      onChange={chat.updateDraft}
      onSubmit={() => void chat.sendMessage()}
      placeholder={placeholder ?? composerPlaceholder(chat.context)}
      busy={chat.sending}
      // A voice call's question is not stopped from the box: the owner talks
      // over it, or ends the call.
      onStop={chat.stoppable ? chat.stop : undefined}
      blocked={chat.hydrating}
      readOnlyWhileBusy
      // Up and Down bring back what the owner sent here (TOM-915); an Undo's
      // line is the server's, not theirs.
      history={chat.messages
        .filter((m) => m.role === 'user' && m.undoNote !== true)
        .map((m) => m.content)}
      above={
        voice === undefined && suggestionRow === null ? undefined : (
          <>
            {voice === undefined ? null : <NeoVoiceStrip voice={voice} />}
            {suggestionRow}
          </>
        )
      }
      talk={
        voice !== undefined && voice.available && !inCall ? (
          <NeoTalkButton onTalk={voice.start} />
        ) : undefined
      }
      dictate={!inCall}
      attachments={chat.attachments}
      menuItems={menuItems}
      inputRef={chat.inputRef}
      describedBy={describedBy}
      testId="neo-composer"
    />
  );
}

export type { NeoRequestContext };

/** A voice call from this chat (TOM-731), on its boat, into its conversation. */
function useChatVoice(chat: NeoConversation): NeoVoice {
  // A Try it guest is refused voice, and its status read too (TOM-868). With
  // no boat the hook offers no Talk and reads nothing.
  const guest = useDemoGuest();
  return useNeoVoice({
    boatId: guest ? undefined : chat.boatId,
    turns: chat.voiceTurns,
    answering: chat.voiceAnswering,
    capabilities: chat.capabilities,
  });
}

interface OpenNeoDetail {
  prompt?: string;
  autoSend?: boolean;
  open?: boolean;
  /** "Edit with Neo": the board or the item the panel is opened for. */
  context?: NeoRequestContext;
  /** The titles the panel names, from its opener (TOM-356). */
  subject?: { board?: string; item?: string };
  /**
   * Ask Neo on a panel (TOM-958): Neo asks what to change about the panel
   * titled so, and the owner's reply names `context`. Nothing is sent.
   */
  askAboutPanel?: { title: string };
}

/** Wider than 1024 px, Edit with Neo is a column beside the page (boards-v3.md). */
const NEO_COLUMN_QUERY = '(min-width: 1025px)';

/**
 * Listen for `nm:open-neo`: the dashboard reveal (phase 7) and other pages
 * hand a seeded prompt to the chat this way, so they never hold a reference
 * to it.
 */
function useOpenNeoEvent(onOpen: (detail: OpenNeoDetail | undefined) => void) {
  const handlerRef = useRef(onOpen);
  handlerRef.current = onOpen;
  useEffect(() => {
    function onOpenNeo(e: Event) {
      handlerRef.current((e as CustomEvent<OpenNeoDetail | undefined>).detail);
    }
    window.addEventListener('nm:open-neo', onOpenNeo);
    return () => window.removeEventListener('nm:open-neo', onOpenNeo);
  }, []);
}

export function NeoChat({
  inlineLauncher = false,
  launcher = true,
  editOnly = false,
}: NeoChatProps = {}) {
  const { boatId } = useParams();
  const chrome = useContext(BoatChromeContext);
  const [open, setOpen] = useState(false);
  const chat = useNeoConversation({ boatId, active: open });
  const voice = useChatVoice(chat);
  const panelRef = useRef<HTMLElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const { inputRef } = chat;
  // Opened by Edit with Neo (TOM-356): what the panel edits, and where its own
  // requests start in the main chat (null until the first is sent).
  const [edit, setEdit] = useState<NeoEditTarget | null>(null);
  const [editFrom, setEditFrom] = useState<number | null>(null);
  const wide = useMediaQuery(NEO_COLUMN_QUERY);
  // On a desktop the panel floats over the page and is not modal: the board
  // keeps its width and columns and changes in place under it (TOM-415); on a
  // phone or tablet it is a sheet.
  const docked = open && edit !== null && wide;
  // Over the page, the panel is the Edit chat (TOM-527): it drags and resizes.
  const frame = useEditChatFrame(docked, panelRef);

  useOpenNeoEvent((detail) => {
    // The Edit chat takes board edits only: Ask Neo on a panel goes to the main chat (TOM-713).
    if (editOnly && !isBoardEditContext(detail?.context)) return;
    const prompt = detail?.prompt;
    // `open:false` composes in the background so the dashboard's building
    // state — not the chat panel — is what the owner sees (empty-until-Neo).
    const shouldOpen = detail?.open !== false;
    if (shouldOpen) setOpen(true);
    // Ask Neo on a panel (TOM-958): the main chat, not an edit of the board.
    if (detail?.askAboutPanel !== undefined && detail.context !== undefined) {
      chat.setRequestContext(null);
      setEdit(null);
      setEditFrom(null);
      chat.askAboutPanel(detail.askAboutPanel.title, detail.context);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    // Opened for a board or an item, the panel names it
    // with each request until it closes; opened any other way, it names nothing.
    const context = detail?.context ?? null;
    chat.setRequestContext(context);
    setEdit(
      context === null
        ? null
        : {
            context,
            boardTitle: detail?.subject?.board ?? null,
            itemTitle: detail?.subject?.item ?? null,
          }
    );
    setEditFrom(null);
    if (typeof prompt === 'string' && prompt.length > 0) {
      chat.seed(prompt, detail?.autoSend === true);
    }
    if (shouldOpen) requestAnimationFrame(() => inputRef.current?.focus());
  });

  const { setRequestContext } = chat;
  useEffect(() => {
    if (open) return;
    setRequestContext(null);
    setEdit(null);
    setEditFrom(null);
  }, [open, setRequestContext]);

  // The board's selection in edit mode is the Edit chat's scope (TOM-670): it
  // changes the context and the chips in place, and keeps the thread, the
  // earlier mark and focus where they are. Only the open Edit chat for that
  // board takes it.
  const scopeRef = useRef<(detail: NeoEditScopeDetail) => void>(() => {});
  scopeRef.current = (detail) => {
    if (!open || edit === null || edit.context.board !== detail.context.board) return;
    setRequestContext(detail.context);
    setEdit({
      context: detail.context,
      boardTitle: detail.boardTitle,
      itemTitle: detail.selection.length === 1 ? detail.selection[0]!.title : null,
      selection: detail.selection,
    });
  };
  useEffect(() => {
    const onScope = (e: Event) => scopeRef.current((e as CustomEvent<NeoEditScopeDetail>).detail);
    window.addEventListener(NEO_EDIT_SCOPE_EVENT, onScope);
    return () => window.removeEventListener(NEO_EDIT_SCOPE_EVENT, onScope);
  }, []);

  // However the Edit chat stops being one (its close button, Escape, a link
  // followed out of it, or an opening for something else), it tells the board,
  // as Done closes it (TOM-546). Floating beside the board, that ends edit mode
  // (TOM-670); as a phone's sheet it does not, since closing the sheet is how
  // an owner reaches the board's own controls under it.
  const editChatOpen = open && edit !== null;
  const wasEditChat = useRef<{ floating: boolean } | null>(null);
  useEffect(() => {
    if (editChatOpen) {
      wasEditChat.current = { floating: docked };
      return;
    }
    const was = wasEditChat.current;
    if (was === null) return;
    wasEditChat.current = null;
    window.dispatchEvent(new CustomEvent(NEO_EDIT_CHAT_CLOSED_EVENT, { detail: was }));
  }, [editChatOpen, docked]);

  // The panel's first request marks where its part of the main chat starts;
  // everything above it shows faded as earlier.
  const { sending, messages } = chat;
  useEffect(() => {
    if (!sending || edit === null || editFrom !== null) return;
    let last = -1;
    messages.forEach((m, i) => {
      if (m.role === 'user') last = i;
    });
    if (last >= 0) setEditFrom(last);
  }, [sending, edit, editFrom, messages]);

  // Pick up the conversation the full page left in the session record while
  // the panel was closed (TOM-195).
  const { syncWithSessionRecord } = chat;
  const syncRef = useRef(syncWithSessionRecord);
  syncRef.current = syncWithSessionRecord;
  useEffect(() => {
    if (open) syncRef.current();
  }, [open]);

  function closeNeo() {
    setOpen(false);
    requestAnimationFrame(() => launcherRef.current?.focus());
  }

  // The board's Done leaves edit mode and closes the Edit chat with it, the
  // same exit as its close button (TOM-546). The panel closes only when it
  // was opened for editing.
  const closeEditRef = useRef<() => void>(() => {});
  closeEditRef.current = () => {
    if (open && edit !== null) closeNeo();
  };
  useEffect(() => {
    const onCloseNeo = () => closeEditRef.current();
    window.addEventListener('nm:close-neo', onCloseNeo);
    return () => window.removeEventListener('nm:close-neo', onCloseNeo);
  }, []);

  useEffect(() => {
    if (!open || docked) return;
    // Everything beside the chat is background while it is open — the shell's
    // header, rails and page, whichever shell the chat sits in (the app shell,
    // or a boat's, whose page is main.neo-shell). Each keeps the attributes it
    // had, to be put back exactly.
    const panel = panelRef.current;
    const background = [...(panel?.parentElement?.children ?? [])]
      .filter(
        (element): element is HTMLElement =>
          element instanceof HTMLElement &&
          element !== panel &&
          !element.classList.contains('neo-scrim')
      )
      .map((element) => ({
        element,
        inert: element.hasAttribute('inert'),
        ariaHidden: element.getAttribute('aria-hidden'),
      }));
    // With no launcher to return to, focus goes back to whatever opened the
    // panel (a page's "Ask Neo" button, say).
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('neo-modal-open');
    for (const { element } of background) {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    }

    const focusFrame = requestAnimationFrame(() => {
      const desktopPointer =
        window.matchMedia('(pointer: fine)').matches && window.innerWidth > 768;
      if (desktopPointer) {
        inputRef.current?.focus();
      } else {
        panelRef.current?.focus();
      }
    });
    return () => {
      cancelAnimationFrame(focusFrame);
      if (!launcher && opener?.isConnected === true) {
        requestAnimationFrame(() => opener.focus());
      }
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('neo-modal-open');
      for (const { element, inert, ariaHidden } of background) {
        if (!inert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      }
    };
  }, [open, docked, inputRef, launcher]);

  // Over the page: nothing is made inert, and the page keeps its scroll and its
  // width (TOM-415). The board scrolls a changed item clear of the card itself;
  // `neo-float-open` gives the page room below its last row to scroll into.
  useEffect(() => {
    if (!docked) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.classList.add('neo-float-open');
    const focusFrame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.classList.remove('neo-float-open');
      // Focus left with the panel: back to whatever opened it.
      requestAnimationFrame(() => {
        const lost = document.activeElement === null || document.activeElement === document.body;
        if (lost && opener?.isConnected === true) opener.focus();
      });
    };
  }, [docked, inputRef]);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: globalThis.KeyboardEvent) {
      const panel = panelRef.current;
      if (event.key === 'Escape') {
        // Beside the page, Escape closes the panel from inside it only.
        if (docked && (panel === null || !panel.contains(document.activeElement))) return;
        event.preventDefault();
        closeNeo();
        return;
      }
      // Beside the page there is no trap: Tab moves on into the page.
      if (docked || event.key !== 'Tab') return;
      if (panel === null) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, docked]);

  if (!open) {
    if (!launcher) return null;
    return (
      <button
        ref={launcherRef}
        type="button"
        className={`neo-launcher${inlineLauncher ? ' neo-launcher--inline' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open Neo chat"
        title="Open Neo"
      >
        <span className="neo-launcher-mark">
          <NeoMarkIcon />
        </span>
        <span className="neo-launcher-label">Ask Neo</span>
      </button>
    );
  }

  const boat = chrome?.boat ?? null;
  const offline =
    boat !== null &&
    getBoatPresence(boat.last_contacted_at, boat.created_at, chrome?.lastSampleAtMs, Date.now()) ===
      'offline';
  const copy = edit !== null ? neoPanelCopy(edit, boat?.display_name ?? null, offline) : null;
  const sheet = copy !== null && !docked;

  const contents = (
    <>
      <header className="neo-panel-head" {...(docked ? frame.headerProps : {})}>
        <div className="neo-title">
          <span className="neo-avatar">
            <NeoMarkIcon />
          </span>
          <div>
            <strong id="neo-title">{docked ? 'Edit chat' : sheet ? copy.sheetTitle : 'Neo'}</strong>
            {copy === null ? null : sheet ? (
              <small className="neo-panel-scope">{copy.sheetScope}</small>
            ) : (
              <span className="neo-panel-chip" data-testid="neo-panel-chip">
                {copy.chip}
              </span>
            )}
          </div>
        </div>
        <div className="edit-chat-head__actions">
          {docked ? frame.menu : null}
          <button
            type="button"
            className="neo-icon-button"
            onClick={closeNeo}
            aria-label="Close Neo chat"
            title="Close"
          >
            <CloseIcon />
          </button>
        </div>
      </header>

      {docked && edit !== null && (edit.selection?.length ?? 0) > 0 ? (
        // The panels selected on the board, the scope of a request (TOM-670).
        <ul
          className="neo-panel-selection"
          aria-label="Selected panels"
          data-testid="neo-panel-selection"
        >
          {edit.selection!.map((panel) => (
            <li key={panel.id} className="neo-panel-selected">
              <span className="neo-panel-selected__title">{panel.title}</span>
              <button
                type="button"
                className="neo-panel-selected__remove"
                aria-label={`Unselect ${panel.title}`}
                title="Unselect"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent(NEO_EDIT_UNSELECT_EVENT, { detail: { id: panel.id } })
                  )
                }
              >
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <NeoThread
        chat={chat}
        noticesInThread
        // Following the notice's link closes the chat over the page.
        onRecallLinkFollowed={() => setOpen(false)}
        // The button goes with the notice: keep focus in the panel.
        onRecallNoticeDismissed={() => panelRef.current?.focus()}
        // Open board shows the board in place of the chat over the page.
        onOpenBoard={() => setOpen(false)}
        edit={
          copy === null
            ? undefined
            : {
                earlierCount: editFrom ?? chat.messages.length,
                loading: copy.loading,
                error: copy.error,
                onDone: closeNeo,
              }
        }
      />

      <NeoComposer
        chat={chat}
        describedBy="neo-privacy-note"
        placeholder={copy?.field}
        // Edit with Neo changes a board; a call is for asking.
        voice={edit === null ? voice : undefined}
      />

      <p className="neo-privacy-note" id="neo-privacy-note">
        {copy !== null
          ? copy.footer
          : 'Neo is your boat assistant. It answers from your boat’s live data.'}
      </p>
    </>
  );

  if (docked) {
    return (
      <aside
        ref={panelRef}
        className="neo-panel neo-panel--float"
        aria-labelledby="neo-title"
        aria-describedby="neo-privacy-note"
        data-testid="neo-panel"
        tabIndex={-1}
        style={frame.style}
      >
        {contents}
        {frame.handles}
      </aside>
    );
  }

  return (
    <>
      <div
        className={`neo-scrim${sheet ? ' neo-scrim--sheet' : ''}`}
        aria-hidden="true"
        onClick={closeNeo}
      />
      <section
        ref={panelRef}
        className={`neo-panel${sheet ? ' neo-panel--sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="neo-title"
        aria-describedby="neo-privacy-note"
        data-testid="neo-panel"
        tabIndex={-1}
      >
        {contents}
      </section>
    </>
  );
}

/** Behind the answer's title: the answer's conclusion, else its question (TOM-710). */
function behindTitle(messages: readonly ChatMessage[], answer: ChatMessage): string {
  const conclusion = neoConclusion(answer.content).conclusion;
  if (conclusion !== null) return conclusion;
  const at = messages.indexOf(answer);
  const question = messages
    .slice(0, at < 0 ? 0 : at)
    .reverse()
    .find((m) => m.role === 'user');
  return question?.content.trim() || 'This answer';
}

// Lucide message-square-plus, for the chat box's "New chat".
function NewChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6" />
      <path d="M9 10h6" />
    </svg>
  );
}

/**
 * Behind the answer sits beside the chat only when the page has room for both
 * (TOM-710): the desktop web. Mike, 17 Oct: not at phone width.
 */
const BEHIND_MIN_WIDTH = 900;

/**
 * The slide-in panel widens to the left for Behind the answer (slide-chat.css)
 * only in a window that has room for the thread and the record side by side
 * beside the sidebar.
 */
const PANEL_SOURCES_QUERY = '(min-width: 1100px)';

/**
 * True while the element the chat page sits in is at least `min` px wide: a
 * container query on the page's own room, which the shell's sidebar shares.
 */
function useRoomFor(min: number): [(node: HTMLElement | null) => void, boolean] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [room, setRoom] = useState(false);
  useEffect(() => {
    const parent = node?.parentElement ?? null;
    if (parent === null) return;
    // A hidden frame (the slide-in chat while it is out, TOM-738) has no
    // width: it keeps the room it had, so nothing beside the chat unmounts
    // and remounts, taking the focus, when it slides back in.
    const update = () => {
      if (parent.clientWidth === 0) return;
      setRoom(parent.clientWidth >= min);
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(parent);
    return () => observer?.disconnect();
  }, [node, min]);
  return [setNode, room];
}

/**
 * The boat's name for a chat's header (TOM-709): the boat the shell shows, or
 * the one the chat's context names.
 */
function useChatBoatName(context: NeoContext | null): string | null {
  const chrome = useContext(BoatChromeContext);
  const shell = chrome?.boat?.display_name;
  if (shell !== undefined && shell.trim().length > 0) return shell;
  return context !== null && context.boats.length === 1 ? context.boats[0]!.displayName : null;
}

/**
 * Neo as the page itself (TOM-195): the boat's chat full height, with no
 * launcher and no sheet. The thread scrolls inside the page and the composer
 * stays at the bottom of it. The page needs a parent of definite height (a
 * full-height flex child); it reads nothing from the shell.
 *
 * Without `conversationId` this is the main chat: it continues the
 * conversation this tab keeps for the boat, the same one the chat panel
 * continues. With one, it opens that conversation from the Chats page and
 * leaves the main chat's record alone.
 */
/** A "Show RPM" chip switches the chart card's view (TOM-711). */
const NEO_PAGE_ANSWER_HOOKS: NeoAnswerViewHooks = { showView: selectNeoChartView };

export function NeoChatPage({
  boatId,
  conversationId,
  fresh = false,
}: {
  boatId: string;
  conversationId?: string;
  /** `neo/new`: a separate chat, which takes its own address once Neo answers (TOM-517). */
  fresh?: boolean;
}) {
  const navigate = useNavigate();
  const chat = useNeoConversation({
    boatId,
    active: true,
    routeConversationId: conversationId,
    fresh,
    onStarted: (id) => navigate(`/dashboard/boats/${boatId}/neo/${id}`, { replace: true }),
    answers: true,
  });
  const voice = useChatVoice(chat);
  const { inputRef } = chat;

  // A page that hands Neo a prompt reaches the page as it reaches the panel.
  useOpenNeoEvent((detail) => {
    // Ask Neo on a panel beside the chat (TOM-958): Neo asks about it here.
    if (detail?.askAboutPanel !== undefined && detail.context !== undefined) {
      chat.askAboutPanel(detail.askAboutPanel.title, detail.context);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    const prompt = detail?.prompt;
    if (typeof prompt === 'string' && prompt.length > 0) {
      chat.seed(prompt, detail?.autoSend === true);
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  });

  function newChat() {
    // A separate chat at `neo/new`; the main chat stays as it is (TOM-517).
    chat.startNewChat();
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  return (
    <NeoChatWorkspace
      chat={chat}
      voice={voice}
      main={conversationId === undefined && !fresh}
      onNewChat={newChat}
    />
  );
}

/**
 * The chat page's body (TOM-195, TOM-357, TOM-710): the header, the thread and
 * the composer, with a board an answer made, or Behind the answer, beside
 * them where there is room. The Chats page's chat and the chat that slides in
 * over a desktop page (TOM-738) are this one frame.
 */
function NeoChatWorkspace({
  chat,
  voice,
  main,
  onNewChat: newChat,
  behindByDefault = false,
  slideIns = 0,
  suggestions,
  narrow = false,
  headerActions,
}: {
  chat: NeoConversation;
  voice: NeoVoice;
  /**
   * The slide-in panel (TOM-920): no board column beside the thread. Sources
   * and Behind the answer stay where the window has room for the panel to
   * widen for them (PANEL_SOURCES_QUERY).
   */
  narrow?: boolean;
  /** More buttons at the end of the header: the docked chat's. */
  headerActions?: ReactNode;
  /** The mode's suggested questions above the box: the slide-in's (TOM-741). */
  suggestions?: readonly string[];
  /** The main chat (the header says so), not one from Chats. */
  main: boolean;
  onNewChat: () => void;
  /**
   * Behind the answer opens beside the thread by itself, on the newest answer
   * with something to show, until the owner closes it (TOM-738).
   */
  behindByDefault?: boolean;
  /** Counts the chat's slide-ins: each one opens Behind the answer by default afresh. */
  slideIns?: number;
}) {
  const { inputRef } = chat;
  const boatName = useChatBoatName(chat.context);

  // The conversation workspace (TOM-357, boards-v3.md): on a desktop, an answer
  // whose turn made or changed a board opens that board in a column beside the
  // chat, on the turn's first record's board. While it is open, requests name
  // it; closing it names nothing. A phone keeps the receipt's Open board.
  const deskWide = useMediaQuery(NEO_COLUMN_QUERY);
  const desk = deskWide && !narrow;
  const [column, setColumn] = useState<{ slug: string; title: string } | null>(null);
  // Hidden from its toggle (TOM-414): the chat takes the full width and the
  // board stays loaded, so the same button brings it back as it was.
  const [boardHidden, setBoardHidden] = useState(false);
  const { liveReceiptId, messages, setRequestContext } = chat;
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (liveReceiptId === null || openedFor.current === liveReceiptId) return;
    const boards = messages.find((m) => m.id === liveReceiptId)?.receipt?.boards ?? [];
    const first = boards.find((c) => c.slug !== '');
    if (first === undefined) return;
    openedFor.current = liveReceiptId;
    // A board the turn removed again has nothing to show.
    const last = boards.filter((c) => c.slug === first.slug).at(-1)!;
    if (last.version === null) return;
    setColumn({ slug: first.slug, title: last.boardTitle ?? first.boardTitle ?? first.slug });
    // A board an answer just made or changed is shown, whatever the last one was.
    setBoardHidden(false);
  }, [liveReceiptId, messages]);
  // Behind the answer (TOM-710): the selected answer's record beside the chat,
  // in the right slot the board column uses. The last one opened has the slot.
  const [roomRef, roomFor] = useRoomFor(BEHIND_MIN_WIDTH);
  const panelRoom = useMediaQuery(PANEL_SOURCES_QUERY);
  const roomy = narrow ? panelRoom : roomFor;
  const [behind, setBehind] = useState<{
    answerId: string;
    evidenceId?: string;
    /** Each open counts, so a chip clicked again brings its item back into view. */
    nonce: number;
    /** Moved there by a new answer (TOM-724): the focus stays where the owner is typing. */
    followed?: boolean;
  } | null>(null);
  const [slot, setSlot] = useState<'board' | 'behind'>('board');
  useEffect(() => {
    if (column !== null) setSlot('board');
  }, [column]);
  const behindMessage =
    behind === null ? undefined : messages.find((m) => m.id === behind.answerId);
  // Open, whether or not the window is wide enough to show it right now.
  const behindOpen =
    slot === 'behind' && behindMessage !== undefined && neoAnswerHasDetails(behindMessage.answer);
  const showingBehind = roomy && behindOpen;
  const openBehind = (answerId: string, evidenceId?: string) => {
    const message = messages.find((m) => m.id === answerId);
    if (message === undefined || !neoAnswerHasDetails(message.answer)) return;
    setBehind((was) => ({
      answerId,
      ...(evidenceId === undefined ? {} : { evidenceId }),
      nonce: (was?.nonce ?? 0) + 1,
    }));
    setSlot('behind');
  };
  // Closed by the owner, a pane open by default stays closed until the chat
  // next slides in (TOM-738).
  const [behindDismissed, setBehindDismissed] = useState(false);
  useEffect(() => setBehindDismissed(false), [slideIns]);
  const closeBehind = () => {
    setBehind(null);
    setSlot('board');
    setBehindDismissed(true);
  };
  const toggleBehind = (answerId: string) => {
    if (showingBehind && behind?.answerId === answerId) closeBehind();
    else openBehind(answerId);
  };
  // The chips sit under the newest answer: whether it has a record to show.
  const newestAnswer = groupNeoAnswers(messages).at(-1)?.answer ?? null;
  const newestAnswerHasDetails = neoAnswerHasDetails(newestAnswer?.answer);
  // The header's Sources opens the newest answer that has a record to show.
  const newestWithDetailsId = neoNewestAnswerWithDetails(messages);
  // An open pane follows each new answer with something to show (TOM-724); a
  // reply with nothing leaves it, and a closed pane stays closed. A narrow
  // window only hides the pane, so it still follows and comes back current.
  const seenNewestWithDetails = useRef(newestWithDetailsId);
  useEffect(() => {
    const target = neoBehindFollowTarget({
      open: behindOpen,
      shownId: behind?.answerId ?? null,
      seenNewestId: seenNewestWithDetails.current,
      newestId: newestWithDetailsId,
    });
    seenNewestWithDetails.current = newestWithDetailsId;
    if (target === null) return;
    // The focus stays where the owner is, unless it is in the pane being
    // replaced: then the new pane takes it, so it never drops to the page.
    const inPane = document.activeElement?.closest('.neo-behind') != null;
    setBehind((was) => ({
      answerId: target,
      nonce: (was?.nonce ?? 0) + 1,
      ...(inPane ? {} : { followed: true }),
    }));
  }, [newestWithDetailsId, behindOpen, behind?.answerId]);
  // Open by default (TOM-738): the newest answer with something to show, as
  // soon as there is one, with the focus left where the owner is.
  const boardInSlot = slot === 'board' && column !== null;
  useEffect(() => {
    const target = neoBehindDefaultTarget({
      byDefault: behindByDefault,
      dismissed: behindDismissed,
      open: behindOpen || boardInSlot,
      newestId: newestWithDetailsId,
    });
    if (target === null) return;
    setBehind((was) => ({ answerId: target, nonce: (was?.nonce ?? 0) + 1, followed: true }));
    setSlot('behind');
  }, [behindByDefault, behindDismissed, behindOpen, boardInSlot, newestWithDetailsId]);
  // Each slide-in moves it to the newest answer with something to show
  // (TOM-741, as the iPad does), wherever the owner left it before the chat
  // slid out: opening it, where it opens by default, or where the owner left
  // it open (the slide-in panel, whose Sources opens it). A board an answer
  // made keeps the slot.
  const slideInsSeen = useRef(slideIns);
  useEffect(() => {
    if (slideIns === slideInsSeen.current) return;
    slideInsSeen.current = slideIns;
    const target = neoBehindSlideInTarget({
      byDefault: behindByDefault || behindOpen,
      // A board the owner hid shows nothing: the pane takes the slot.
      boardShown: boardInSlot && !boardHidden,
      shownId: behindOpen ? (behind?.answerId ?? null) : null,
      newestId: newestWithDetailsId,
    });
    if (target === null) return;
    setBehind((was) => ({ answerId: target, nonce: (was?.nonce ?? 0) + 1, followed: true }));
    setSlot('behind');
  }, [
    slideIns,
    behindByDefault,
    boardInSlot,
    boardHidden,
    behindOpen,
    behind?.answerId,
    newestWithDetailsId,
  ]);

  const shown = desk && !showingBehind ? column : null;
  useEffect(() => {
    setRequestContext(shown === null ? null : boardBesideChatContext(shown.slug));
  }, [shown, setRequestContext]);

  const page = (
    <section className="neo-page neo-page--headed" data-testid="neo-page" aria-label="Neo">
      <NeoChatHeader
        boatName={boatName}
        title={neoChatTitle(messages, { main })}
        onNewChat={newChat}
        actions={headerActions}
        sources={
          roomy && newestWithDetailsId !== null
            ? {
                open: showingBehind,
                onToggle: () => (showingBehind ? closeBehind() : openBehind(newestWithDetailsId)),
              }
            : undefined
        }
      />
      <NeoThread
        chat={chat}
        noticesInLog
        sources={
          roomy
            ? {
                selected: showingBehind ? (behind?.answerId ?? null) : null,
                has: (m) => neoAnswerHasDetails(m.answer),
                open: toggleBehind,
              }
            : undefined
        }
        // A chart's view switch (TOM-711) always; a "Show the evidence" chip
        // opens the pane only where the newest answer has a record to show,
        // otherwise it asks Neo (TOM-709, TOM-710).
        answerHooks={
          roomy && newestAnswerHasDetails
            ? { ...NEO_PAGE_ANSWER_HOOKS, inspectEvidence: openBehind }
            : NEO_PAGE_ANSWER_HOOKS
        }
      />
      {chat.greetingPending ? (
        <p className="neo-greeting-pending" role="status" data-testid="neo-greeting-pending">
          <span className="neo-greeting-pending__dot" aria-hidden="true" />
          Neo is checking on {boatName ?? 'the boat'}…
        </p>
      ) : null}
      <div className="neo-page-composer">
        <NeoComposer
          chat={chat}
          onNewChat={newChat}
          placeholder={shown === null ? undefined : `Ask Neo to change ${shown.title}`}
          voice={voice}
          // Docked, the newest answer's own questions stand in for the mode's
          // (TOM-920): one row of chips, not two.
          suggestions={
            narrow && (newestAnswer?.answer?.followups.length ?? 0) > 0 ? undefined : suggestions
          }
        />
        {desk && shown === null && !messages.some((m) => m.role === 'user') ? (
          <p className="neo-page-hint">
            An answer that makes or changes a board opens the board beside the chat.
          </p>
        ) : null}
      </div>
    </section>
  );
  // One frame whatever sits beside the chat, so the chat itself never
  // remounts when the right slot fills or empties: the owner's place in the
  // thread, the draft and the focus to return to all stay (TOM-710).
  const beside: 'behind' | 'board' | null =
    showingBehind && behindMessage?.answer !== undefined
      ? 'behind'
      : shown !== null
        ? 'board'
        : null;
  return (
    <div
      ref={roomRef}
      className={`neo-workspace${
        beside === null
          ? ' neo-workspace--alone'
          : beside === 'behind'
            ? ' neo-workspace--behind'
            : boardHidden
              ? ' neo-workspace--board-hidden'
              : ''
      }`}
      {...(beside === 'board' ? { 'data-testid': 'neo-workspace' } : {})}
    >
      {page}
      {beside === 'behind' && behindMessage?.answer !== undefined ? (
        <NeoBehindAnswer
          // One pane per answer: another answer's opens afresh, with its own
          // way back for the focus.
          key={behindMessage.id}
          record={behindMessage.answer}
          title={behindTitle(messages, behindMessage)}
          focusEvidence={behind?.evidenceId}
          focusNonce={behind?.nonce}
          keepFocus={behind?.followed === true}
          onClose={closeBehind}
        />
      ) : null}
      {beside === 'board' && shown !== null && boardHidden ? (
        // The same button the board's head carries, in the same corner.
        <button
          type="button"
          className="tb-icon-btn neo-workspace__show"
          aria-label={`Show ${shown.title}`}
          aria-expanded="false"
          title="Show board"
          data-testid="board-column-toggle"
          onClick={() => setBoardHidden(false)}
        >
          <Icon name="panel-right" size={18} />
        </button>
      ) : null}
      {beside === 'board' && shown !== null ? (
        <aside className="neo-workspace__board" aria-label={shown.title} hidden={boardHidden}>
          <Suspense fallback={<div className="board-v3 board-column" aria-busy="true" />}>
            <BoardColumnView
              key={shown.slug}
              slug={shown.slug}
              onClose={() => {
                setColumn(null);
                setBoardHidden(false);
                requestAnimationFrame(() => inputRef.current?.focus());
              }}
              onHide={() => setBoardHidden(true)}
            />
          </Suspense>
        </aside>
      ) : null}
    </div>
  );
}

/**
 * Neo's main chat on the boat's home (TOM-511, TOM-520; the handoff's 2a): the
 * same conversation as the Neo page, the tab's main chat, with the page's own
 * thread and composer and no board column (a receipt's Open board opens the
 * board's page, and the board on home changes in place).
 *
 * Home keeps it closed until the owner opens it from the sparkle (BoatHome).
 * It swoops in from the right: beside the board above 768 px, over the whole
 * screen at 768 px and under. Its header's close icon, or Escape, swoops it
 * off again. It stays mounted while closed, so it keeps its conversation,
 * hears `nm:open-neo` (which opens it) and tells home when Neo answered while
 * it was closed (the sparkle's unread dot).
 */
export function NeoHomeChat({
  boatId,
  open,
  fullScreen,
  onOpenChange,
  onUnread,
  boardEditsElsewhere = false,
  suggestions,
}: {
  boatId: string;
  open: boolean;
  /**
   * On a board page a request that names the board goes to its Edit chat, not
   * here (TOM-547).
   */
  boardEditsElsewhere?: boolean;
  /**
   * The mode's suggested questions, in a row above the message box, as the
   * slide-in chat has them (TOM-741, TOM-817).
   */
  suggestions?: readonly string[];
  /** At 768 px and under: over the whole screen, not beside the board. */
  fullScreen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Neo answered while the chat was closed. */
  onUnread: () => void;
}) {
  const chat = useNeoConversation({ boatId, active: true, answers: true });
  const voice = useChatVoice(chat);
  const { inputRef, sending, scrollRef, setRequestContext } = chat;
  const boatName = useChatBoatName(chat.context);
  const openRef = useRef(open);
  openRef.current = open;
  const callbacks = useRef({ onOpenChange, onUnread });
  callbacks.current = { onOpenChange, onUnread };

  // A request opened for a board names its target, as
  // the panel's does, for that request only: home's chat is the main chat.
  // A target no request used goes when the chat closes or a new chat starts,
  // so a later turn from the sparkle names nothing: a board edit loads the
  // board editor, and an unrelated turn must not (TOM-543, cloud ADR 0341 §4).
  const contextTurn = useRef(false);
  const dropTarget = useCallback(() => {
    if (!contextTurn.current) return;
    contextTurn.current = false;
    setRequestContext(null);
  }, [setRequestContext]);
  useOpenNeoEvent((detail) => {
    if (boardEditsElsewhere && isBoardEditContext(detail?.context)) return;
    // Ask Neo on a panel (TOM-958): Neo asks what to change about it, and the
    // owner's reply names it. Nothing is sent.
    if (detail?.askAboutPanel !== undefined && detail.context !== undefined) {
      chat.askAboutPanel(detail.askAboutPanel.title, detail.context);
      callbacks.current.onOpenChange(true);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    const context = detail?.context ?? null;
    setRequestContext(context);
    contextTurn.current = context !== null;
    const prompt = detail?.prompt;
    if (typeof prompt === 'string' && prompt.length > 0) {
      chat.seed(prompt, detail?.autoSend === true);
    }
    // `open: false` composes in the background: the chat stays as it is.
    if (detail?.open !== false) {
      callbacks.current.onOpenChange(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  });

  // A message that reached the main chat from elsewhere (an alarm, TOM-517)
  // puts the dot on the sparkle while the chat is closed. It never opens it.
  const seenArrivals = useRef(chat.arrivals);
  useEffect(() => {
    if (chat.arrivals === seenArrivals.current) return;
    seenArrivals.current = chat.arrivals;
    if (!openRef.current) callbacks.current.onUnread();
  }, [chat.arrivals]);

  // A turn's end clears a request's target, and, with the chat closed, tells
  // home there is something new.
  const wasSending = useRef(false);
  useEffect(() => {
    if (!sending && wasSending.current) {
      dropTarget();
      if (!openRef.current) callbacks.current.onUnread();
    }
    wasSending.current = sending;
  }, [sending, dropTarget]);

  // Closing the chat drops a target its next request has not used (TOM-543).
  useEffect(() => {
    if (!open) dropTarget();
  }, [open, dropTarget]);

  // Swooping in, the focus goes into the chat and the thread shows its newest
  // message. Swooping out, it stays drawn until the slide ends, then hides.
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) {
      setShown(true);
      // At once, not on a frame: a window in the background runs none.
      inputRef.current?.focus({ preventScroll: true });
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
      return;
    }
    if (reducedMotion) setShown(false);
  }, [open, reducedMotion, inputRef, scrollRef]);

  function newChat() {
    // A new chat starts with no target (TOM-543).
    dropTarget();
    chat.startNewChat();
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  return (
    <aside
      className={`neo-home-chat${fullScreen ? ' neo-home-chat--full' : ''}`}
      data-state={open ? 'open' : 'closing'}
      aria-label="Neo, main chat"
      data-testid="home-chat"
      hidden={!open && !shown}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && !open) setShown(false);
      }}
      onKeyDown={(e) => {
        // Escape closes the chat, once a menu inside it has closed itself.
        if (e.key !== 'Escape' || e.defaultPrevented) return;
        if (e.currentTarget.querySelector('.chat-composer__menu') !== null) return;
        e.preventDefault();
        onOpenChange(false);
      }}
    >
      <header className="neo-home-chat__head">
        <span className="neo-home-chat__mark" aria-hidden="true">
          <Icon name="tombolo" size={22} />
        </span>
        <div className="neo-home-chat__titles">
          <strong>Neo</strong>
          {/* The boat and the conversation (TOM-709); a narrow panel breaks the
              line between them. Home's chat is always the main chat. */}
          <small>
            {boatName === null ? null : <span>{boatName} ·</span>} <span>Main chat</span>
          </small>
        </div>
        <button
          type="button"
          className="tb-icon-btn neo-home-chat__btn"
          aria-label="New chat"
          title="New chat"
          data-testid="home-chat-new"
          onClick={newChat}
        >
          <Icon name="plus" size={20} />
        </button>
        <Link
          className="tb-icon-btn neo-home-chat__btn"
          to={`/dashboard/boats/${boatId}/neo`}
          aria-label="Open the full chat"
          title="Full chat"
          data-testid="home-chat-full"
        >
          <Icon name="expand" size={19} />
        </Link>
        <Link
          className="tb-icon-btn neo-home-chat__btn"
          to={`/dashboard/boats/${boatId}/chats`}
          aria-label="All chats"
          title="All chats"
          data-testid="home-chat-all"
        >
          <Icon name="chats" size={20} />
        </Link>
        <button
          type="button"
          className="tb-icon-btn neo-home-chat__btn"
          aria-label="Close Neo"
          title="Close Neo"
          data-testid="home-chat-close"
          onClick={() => onOpenChange(false)}
        >
          <Icon name="panel-right-close" size={20} />
        </button>
      </header>
      <div className="neo-page neo-home-chat__page">
        <div className="neo-home-chat__thread">
          <NeoThread chat={chat} noticesInLog />
        </div>
        <div className="neo-page-composer">
          <NeoComposer chat={chat} onNewChat={newChat} voice={voice} suggestions={suggestions} />
        </div>
      </div>
    </aside>
  );
}

/**
 * Neo's main chat sliding in over a desktop page (TOM-738, after the iPad's
 * TOM-734): the Chats page's thread and composer in a panel over the right of
 * the page, the shape home's chat has on every page (TOM-920). Its Sources
 * widen the panel for Behind the answer, following each new answer (TOM-724),
 * where the window has room. It comes in from the right and goes back out,
 * leaving the page under it as it was; the shell's sparkle opens it and its
 * header closes it (SlideChat).
 *
 * Like home's chat (NeoHomeChat) it stays mounted while closed, so it keeps
 * its conversation, hears `nm:open-neo` (which opens it) and tells the shell
 * when Neo answered while it was closed. On a board page a board edit goes to
 * the Edit chat instead, and the slide-in steps aside for it.
 */
export function NeoSlideChat({
  boatId,
  open,
  slideIns,
  onOpenChange,
  onUnread,
  boardEditsElsewhere = false,
  suggestions,
  panel,
}: {
  boatId: string;
  open: boolean;
  /** The panel's header buttons: Open the full chat and Close (TOM-920). */
  panel: { fullChatHref: string; onClose: () => void };
  /** Counts the slide-ins, so each opens Behind the answer by default afresh. */
  slideIns: number;
  /** The mode's suggested questions, above the message box (TOM-741). */
  suggestions?: readonly string[];
  onOpenChange: (open: boolean) => void;
  /** Neo answered while the chat was closed. */
  onUnread: () => void;
  /** On a board page a request that names the board goes to its Edit chat. */
  boardEditsElsewhere?: boolean;
}) {
  const chat = useNeoConversation({ boatId, active: true, answers: true });
  const voice = useChatVoice(chat);
  const { inputRef, sending, scrollRef, setRequestContext } = chat;
  const openRef = useRef(open);
  openRef.current = open;
  const callbacks = useRef({ onOpenChange, onUnread });
  callbacks.current = { onOpenChange, onUnread };

  // A request opened for a panel names it for that request only, as home's
  // chat does (TOM-543, TOM-713): the target goes when the turn ends, the chat
  // closes or a new chat starts.
  const contextTurn = useRef(false);
  const dropTarget = useCallback(() => {
    if (!contextTurn.current) return;
    contextTurn.current = false;
    setRequestContext(null);
  }, [setRequestContext]);
  useOpenNeoEvent((detail) => {
    if (boardEditsElsewhere && isBoardEditContext(detail?.context)) {
      // The Edit chat opens for it; the slide-in makes way.
      if (openRef.current) callbacks.current.onOpenChange(false);
      return;
    }
    // Ask Neo on a panel (TOM-958): Neo asks what to change about it, and the
    // owner's reply names it. Nothing is sent.
    if (detail?.askAboutPanel !== undefined && detail.context !== undefined) {
      chat.askAboutPanel(detail.askAboutPanel.title, detail.context);
      callbacks.current.onOpenChange(true);
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
      return;
    }
    const context = detail?.context ?? null;
    setRequestContext(context);
    contextTurn.current = context !== null;
    const prompt = detail?.prompt;
    if (typeof prompt === 'string' && prompt.length > 0) {
      chat.seed(prompt, detail?.autoSend === true);
    }
    // `open: false` composes in the background: the chat stays as it is.
    if (detail?.open !== false) {
      callbacks.current.onOpenChange(true);
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }
  });

  // A message that reached the main chat from elsewhere (an alarm, TOM-517),
  // or a turn that ended while the chat was closed, puts the dot on the sparkle.
  const seenArrivals = useRef(chat.arrivals);
  useEffect(() => {
    if (chat.arrivals === seenArrivals.current) return;
    seenArrivals.current = chat.arrivals;
    if (!openRef.current) callbacks.current.onUnread();
  }, [chat.arrivals]);
  const wasSending = useRef(false);
  useEffect(() => {
    if (!sending && wasSending.current) {
      dropTarget();
      if (!openRef.current) callbacks.current.onUnread();
    }
    wasSending.current = sending;
  }, [sending, dropTarget]);
  useEffect(() => {
    if (!open) dropTarget();
  }, [open, dropTarget]);

  // Sliding in, the focus goes into the composer and the thread shows its
  // newest message, once more after the frame the pane beside it lays out in.
  // The slide itself is a CSS transition on the frame, which stays laid out
  // while it is out (TOM-741): no animation end to wait for, so a slide-out
  // that could not run (the frame hidden by edit mode or the Edit chat) never
  // plays later as a flash of the whole chat.
  useEffect(() => {
    if (!open) return;
    // At once, not on a frame: a window in the background runs none.
    inputRef.current?.focus({ preventScroll: true });
    const log = scrollRef.current;
    if (log === null) return;
    log.scrollTo({ top: log.scrollHeight });
    const frame = requestAnimationFrame(() => log.scrollTo({ top: log.scrollHeight }));
    return () => cancelAnimationFrame(frame);
  }, [open, inputRef, scrollRef]);

  function newChat() {
    // A separate chat at `neo/new` (TOM-517), with no target (TOM-543).
    dropTarget();
    chat.startNewChat();
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  return (
    <aside
      id="neo-slide-chat"
      className="neo-slide-chat"
      data-state={open ? 'open' : 'closed'}
      aria-label="Neo, main chat"
      data-testid="home-chat"
      // Out, it stays laid out off the edge (slide-chat.css hides it): nothing
      // in it takes the focus or reaches a screen reader.
      inert={!open}
    >
      <NeoChatWorkspace
        chat={chat}
        voice={voice}
        main
        onNewChat={newChat}
        slideIns={slideIns}
        suggestions={suggestions}
        narrow
        headerActions={
          <>
            <Link
              className="tb-icon-btn neo-chat-head__action"
              to={panel.fullChatHref}
              aria-label="Open the full chat"
              title="Open the full chat"
              data-testid="neo-dock-full-chat"
            >
              <Icon name="maximize" size={18} />
            </Link>
            <button
              type="button"
              className="tb-icon-btn neo-chat-head__action"
              aria-label="Close the chat"
              title="Close the chat"
              data-testid="neo-dock-close"
              onClick={panel.onClose}
            >
              <Icon name="arrow-right" size={20} />
            </button>
          </>
        }
      />
    </aside>
  );
}

// The board page's column, loaded with the board page's code (TOM-357).
const BoardColumnView = lazy(() =>
  import('../pages/Board').then((m) => ({ default: m.BoardColumn }))
);
